import React, { useState, useEffect, useRef } from 'react'
import classNames from 'classnames'
import { DataTable } from 'primereact/datatable'
import { Column } from 'primereact/column'
import { Toast } from 'primereact/toast'
import { Button } from 'primereact/button'
import { FileUpload } from 'primereact/fileupload'
import { Toolbar } from 'primereact/toolbar'
import { Dialog } from 'primereact/dialog'
import { InputText } from 'primereact/inputtext'
import { Dropdown } from 'primereact/dropdown'
import { IconField } from 'primereact/iconfield'
import { InputIcon } from 'primereact/inputicon'
import { Slider } from 'primereact/slider'
import { TabView, TabPanel } from 'primereact/tabview'
import { Tag } from 'primereact/tag';
import { Skeleton } from 'primereact/skeleton';
import { InfoEntry } from '../components/InfoEntry';
import { DeviceDownlinkButton } from '../components/DeviceDownlinkButton';
import * as R from 'ramda'
import { formatUTCTime } from '../utils/format'
import {
  useQuery,
  useMutation,
  useQueryClient,
} from '@tanstack/react-query'
import {
  listAllCompaniesRequest,
  listAllDevicesRequest,
  createDeviceRequest,
  unbindDeviceRequest,
  patchDeviceRequest,
  putAdjustRequest,
  putSensorPreferenceRequest,
  putDefaultSensorPreferenceRequest,
  putProbeTypeRequest
} from '../apis/calyxSaaSDashboardAPI'
import axios from 'axios'
import { StatusCodes } from 'http-status-codes';
import { useStore } from '../stores/GlobalStore'
import { DeviceTypeEnum, DeviceVendorWithModelEnum } from '../types/enums'


/**
 * 這個頁面規劃為只對calyx Y裝置做CRUD,
 * 若有其他種類裝置, 如water meter, scale等,
 * 在另開page做維護
 * @returns
 */
export const CrudCayxYDevices = () => {
  const emptyDevice = {
    serialNumber: '',
    hardwareSerial: '',
    service: 'calyxsensingdatas',
    positionId: undefined,
    companyId: '',
    type: DeviceTypeEnum.CALYX_Y, // 只對calyx Y裝置做CRUD,
    vendorWithModel: DeviceVendorWithModelEnum.CALYX_Y // 只對calyx Y裝置做CRUD,
  }

  const emptyAdjustValues = {
    calyx_x: {
        nh3: { value: [], operator: "" },
        temperature: { value: [], operator: "" },
        humidity: { value: [], operator: "" }
      },
    ec_nh3: {
      nh3: { value: [], operator: "" }
    },
    dyna_co2: {
      co2: { value: [], operator: "" }
    },
    sht30: {
      temperature: { value: [], operator: "" },
      humidity: { value: [], operator: "" }
    },
    scd4x_co2: {
      co2: { value: [], operator: "" },
      temperature: { value: [], operator: "" },
      humidity: { value: [], operator: "" }
    },
    cycfs1: {
      wind_speed: { value: [], operator: "" }
    }
  }

  const emptyDownlinkOption = {
    // Adjust Interval
    pkg5: { payload: [], lora_time: 0 },
    pkg6: { payload: [], lora_time: 0 },
    pkg7: { payload: [], lora_time: 0 },
    pkg11: { // ref uplink pkg7
      regularMeasurementInterval: 15, // 0 ~ 255 minute
      statusReportInterval: 15, // 0 ~ 65536 minute Big-Endian
      sleepN1: 15, // 0 ~ 255
      sleepN2: 15 // 0 ~ 255
    },
    pkg12: { // ref uplink pkg6: nh3_offset_threshold
      nh3Threshold: 100 // 0 ~ 255 ppm
    },
    pkg13: { // ref uplink pkg6: temp_offset_threshold
      temperatureThreshold: 30 // 0 ~ 255 unit 0.1 C
    },
    pkg14: { // ref uplink pkg6: co2_offset_threshold
      co2Threshold: 20 // 0 ~ 255 unit 10ppm
    },
    pkg15: { // ref uplink pkg6: nh3_offset_threshold
      anemometerThreshold: 10 // 0 ~ 255 0.1 m/s
    },
    // Adjust Sensor Type Parameter Package
    pkg16: { // ref uplink pkg5
      sensorAType: 0, // 0 ~ 255
      sensorBType: 0, // 0 ~ 255
      sensorCType: 0 // 0 ~ 255
    },
    noPackages: true
  }

  const deviceServicesDropdown = [
    { label: 'farms (v1)', value: 'farms' },
    { label: 'pigfarm (v1)', value: 'pigfarm' },
    { label: 'enspira (v1)', value: 'enspira_service' },
    { label: 'sensingdatas (v1 hsb)', value: 'sensingdatas' },
    { label: 'calyxsensingdatas (v2 - latest)', value: 'calyxsensingdatas' },
  ]

  const queryClient = useQueryClient()
  const [deviceDownlink, setDeviceDownlink] = useState(emptyDownlinkOption)
  const [companyDropdown, setCompanyDropdown] = useState(null)
  const [companyMap, setCompanyMap] = useState(null)
  const [createOrEditDeviceDialogVisibility, setCreateOrEditDeviceDialogVisibility] = useState(false)
  const [isCreateDeviceMode, setIsCreateDeviceMode] = useState(false)
  const [deleteDeviceDialogVisibility, setDeleteDeviceDialogVisibility] = useState(false)
  const [deviceDetailDialog, setDeviceDetailDialog] = useState(false)
  const [adjustDeviceDialogVisibility, setAdjustDeviceDialogVisibility] = useState(false)
  const [editSensorPreferenceDialogVisibility, setEditSensorPreferenceDialogVisibility] = useState(false)
  const [editProbeTypeDialogVisibility, setEditProbeTypeDialogVisibility] = useState(false)
  const [resetToDeafultSensorPreferenceDialogVisibility, setresetToDeafultSensorPreferenceDialogVisibility] = useState(false)
  const [deviceDownlinkDialog, setDeviceDownlinkDialog] = useState(false)
  const [focusedDevice, setFocusedDevice] = useState(emptyDevice)
  const [selectedDevices, setSelectedDevices] = useState([])
  const [adjustValues, setAdjustValues] = useState(emptyAdjustValues)
  const [editingSensorPreference, setEditingSensorPreference] = useState(null)
  const [editingProbeType, setEditingProbeType] = useState(null)
  const [submitted, setSubmitted] = useState(false)
  const [globalFilter, setGlobalFilter] = useState(null)
  const toast = useRef(null)
  const dt = useRef(null)
  const { state, dispatch } = useStore()

  const allCompaniesQuery = useQuery(['list', 'companies'], () => {
    return listAllCompaniesRequest()
  }, {
    refetchOnWindowFocus: false,
    retry: 0,
    onSuccess: (companies) => {
      const companyMap = companies.reduce((acc, c) => ({ ...acc, [c.id]: c }), {})
      setCompanyMap(companyMap)
      setCompanyDropdown(Object.keys(companyMap).map(cid => ({ label: companyMap[cid].name, value: companyMap[cid].id })))
    },
    onError: (err) => {
      if (axios.isAxiosError(err)) {
        if (err?.response?.status === StatusCodes.UNAUTHORIZED || err?.response?.status === StatusCodes.FORBIDDEN) {
          let countDownSecond = 3;
          toast.current.show({ severity: 'error', summary: `Session expired, going to logout in ${countDownSecond} seconds... Please login again`, life: 3000 });
          setInterval(() => {
            countDownSecond--;
            if (countDownSecond <= 0) {
              dispatch({
                type: 'LOGOUT',
              })
            }
          }, 1000);
        } else if (err?.response?.status === StatusCodes.INTERNAL_SERVER_ERROR) {
          toast.current.show({ severity: 'error', summary: 'Internal server error, please try again later', life: 5000 });
        } else {
          toast.current.show({ severity: 'error', summary: `Unkown Error, status code: ${err?.response?.status}`, life: 5000 });
        }
      } else {
        toast.current.show({ severity: 'error', summary: 'Unkown Error', life: 5000 });
      }
    }
  })

  const allCompanies = React.useMemo(() => {
    return allCompaniesQuery.data ?? []
  }, [allCompaniesQuery.data])

  const allCompaniesMap = React.useMemo(() => {
    return new Map(allCompanies.map((company) => [company.id, company]))
  }, [allCompanies])

  const allPositions = React.useMemo(() => {
    return allCompanies.flatMap((company) => {
      return company.businesses.flatMap((business) => {
        return business.farms.flatMap((farm) => {
          return farm.houses.flatMap((house) => {
            return house.positions.flatMap((position, positionIndex) => {
              return position != null ? [{
                companyId: company.id,
                companyName: company.name,
                businessId: business.id,
                businessName: business.name,
                farmId: farm.id,
                farmName: farm.name,
                houseId: house.id,
                houseName: house.name,
                id: position.id,
                name: ['F1', 'F2', 'F3', 'M1', 'M2', 'M3', 'B1', 'B2', 'B3'][positionIndex] ?? '??',
              }] : []
            })
          })
        })
      })
    })
  }, [allCompanies])

  const allPositionsMap = React.useMemo(() => {
    return new Map(allPositions.map((position) => [position.id, position]))
  }, [allPositions])

  const allDevicesQuery = useQuery(['list', 'devices'], () => {
    return listAllDevicesRequest()
  }, {
    refetchOnWindowFocus: false,
    retry: 0,
    enabled: !!companyMap,
    onSuccess: (allDevices) => {
      const allDevicesWithCompanyName = allDevices.map( device => {
        const dedicatedCompany = companyMap[device.companyId]
        if(dedicatedCompany){
          return {
            ...device,
            companyName: dedicatedCompany.name
          }
        } else {
          return {
            ...device,
            companyName: ''
          }
        }
      })
      queryClient.setQueryData(['list', 'devices'], allDevicesWithCompanyName)
    },
    onError: (err) => {
      if (axios.isAxiosError(err)) {
        if (err?.response?.status === StatusCodes.UNAUTHORIZED || err?.response?.status === StatusCodes.FORBIDDEN) {
          let countDownSecond = 3;
          toast.current.show({ severity: 'error', summary: `Session expired, going to logout in ${countDownSecond} seconds... Please login again`, life: 3000 });
          setInterval(() => {
            countDownSecond--;
            if (countDownSecond <= 0) {
              dispatch({
                type: 'LOGOUT',
              })
            }
          }, 1000);
        } else if (err?.response?.status === StatusCodes.INTERNAL_SERVER_ERROR) {
          toast.current.show({ severity: 'error', summary: 'Internal server error, please try again later', life: 5000 });
        } else {
          toast.current.show({ severity: 'error', summary: `Unkown Error, status code: ${err?.response?.status}`, life: 5000 });
        }
      } else {
        toast.current.show({ severity: 'error', summary: 'Unkown Error', life: 5000 });
      }
    }
  })

  const allDevices = React.useMemo(() => {
    return (allDevicesQuery.data ?? []).map((device) => {
      const { companyId, positionId } = device;

      const {
        name: companyName,
      } = allCompaniesMap.get(companyId) ?? {};
      const {
        businessName,
        farmName,
        houseName,
        name: positionName,
      } = allPositionsMap.get(positionId) ?? {};

      return {
        ...device,
        companyName,
        businessName,
        farmName,
        houseName,
        positionName,
      };
    });
  }, [allCompaniesMap, allDevicesQuery.data, allPositionsMap]);

  const createDeviceMutation = useMutation(requestBody => {
    return createDeviceRequest(requestBody)
  },
    {
      onSuccess: (savedNewDevice) => {
        toast.current.show({ severity: 'success', summary: 'Success', detail: 'New device has been saved', life: 3000 })
        queryClient.setQueryData(['list', 'devices'], staleDevices => {
          savedNewDevice.companyName = companyMap[savedNewDevice.companyId].name || ''
          const newDevices = [savedNewDevice, ...staleDevices]
          return newDevices
        })
        hideCreateOrEditDeviceDialog()
      },
      onError: (err) => {
        if (axios.isAxiosError(err)) {
          if (err?.response?.status === StatusCodes.UNAUTHORIZED || err?.response?.status === StatusCodes.FORBIDDEN) {
            let countDownSecond = 3;
            toast.current.show({ severity: 'error', summary: `Session expired, going to logout in ${countDownSecond} seconds... Please login again`, life: 3000 });
            setInterval(() => {
              countDownSecond--;
              if (countDownSecond <= 0) {
                dispatch({
                  type: 'LOGOUT',
                })
              }
            }, 1000);
          } else if (err?.response?.status === StatusCodes.BAD_REQUEST) {
            toast.current.show({ severity: 'error', summary: err.response?.data?.message, life: 5000 });
          } else if (err?.response?.status === StatusCodes.INTERNAL_SERVER_ERROR) {
            toast.current.show({ severity: 'error', summary: 'Internal server error, please try again later', life: 5000 });
          } else {
            toast.current.show({ severity: 'error', summary: `Unkown Error, status code: ${err?.response?.status}`, life: 5000 });
          }
        } else {
          toast.current.show({ severity: 'error', summary: 'Unkown Error', life: 5000 });
        }
      }
    })

  const unbindDeviceMutation = useMutation(unbindDeviceOption => {
    return unbindDeviceRequest(unbindDeviceOption.positionId, unbindDeviceOption.deviceId)
  },
    {
      onSuccess: (updatedDevice) => {
        toast.current.show({ severity: 'success', summary: 'Success', detail: 'Selected device has been unbinded with position', life: 3000 })
        queryClient.setQueryData(['list', 'devices'], staleDevices => {
          const newDevices = [...staleDevices]
          const idx = newDevices.findIndex(staleDevice => staleDevice.id === updatedDevice.id)
          if (idx >= 0) {
            updatedDevice.companyName = companyMap[updatedDevice.companyId].name || ''
            newDevices[idx] = updatedDevice
            return newDevices
          } else {
            toast.current.show({ severity: 'error', summary: 'Something went wrong, please try again later', life: 5000 });
          }
        })
        hideDeleteDeviceDialog()
      },
      onError: (err) => {
        if (axios.isAxiosError(err)) {
          if (err?.response?.status === StatusCodes.UNAUTHORIZED || err?.response?.status === StatusCodes.FORBIDDEN) {
            let countDownSecond = 3;
            toast.current.show({ severity: 'error', summary: `Session expired, going to logout in ${countDownSecond} seconds... Please login again`, life: 3000 });
            setInterval(() => {
              countDownSecond--;
              if (countDownSecond <= 0) {
                dispatch({
                  type: 'LOGOUT',
                })
              }
            }, 1000);
          } else if (err?.response?.status === StatusCodes.BAD_REQUEST) {
            toast.current.show({ severity: 'error', summary: err.response?.data?.message, life: 5000 });
          } else if (err?.response?.status === StatusCodes.INTERNAL_SERVER_ERROR) {
            toast.current.show({ severity: 'error', summary: 'Internal server error, please try again later', life: 5000 });
          } else {
            toast.current.show({ severity: 'error', summary: `Unkown Error, status code: ${err?.response?.status}`, life: 5000 });
          }
        } else {
          toast.current.show({ severity: 'error', summary: 'Unkown Error', life: 5000 });
        }
      }
    })

  const patchDeviceMutation = useMutation(patchDeviceOption => {
    return patchDeviceRequest(patchDeviceOption.companyId, patchDeviceOption.deviceId, patchDeviceOption.requestBody)
  },
    {
      onSuccess: (updatedDevice) => {
        toast.current.show({ severity: 'success', summary: 'Success', detail: 'Selected device has been updated', life: 3000 })
        queryClient.setQueryData(['list', 'devices'], staleDevices => {
          const newDevices = [...staleDevices]
          const idx = newDevices.findIndex(staleDevice => staleDevice.id === updatedDevice.id)
          if (idx >= 0) {
            updatedDevice.companyName = companyMap[updatedDevice.companyId].name || ''
            newDevices[idx] = updatedDevice
            return newDevices
          } else {
            toast.current.show({ severity: 'error', summary: 'Something went wrong, please try again later', life: 5000 });
          }
        })
        hideCreateOrEditDeviceDialog()
      },
      onError: (err) => {
        if (axios.isAxiosError(err)) {
          if (err?.response?.status === StatusCodes.UNAUTHORIZED || err?.response?.status === StatusCodes.FORBIDDEN) {
            let countDownSecond = 3;
            toast.current.show({ severity: 'error', summary: `Session expired, going to logout in ${countDownSecond} seconds... Please login again`, life: 3000 });
            setInterval(() => {
              countDownSecond--;
              if (countDownSecond <= 0) {
                dispatch({
                  type: 'LOGOUT',
                })
              }
            }, 1000);
          } else if (err?.response?.status === StatusCodes.BAD_REQUEST) {
            toast.current.show({ severity: 'error', summary: err.response?.data?.message, life: 5000 });
          } else if (err?.response?.status === StatusCodes.INTERNAL_SERVER_ERROR) {
            toast.current.show({ severity: 'error', summary: 'Internal server error, please try again later', life: 5000 });
          } else {
            toast.current.show({ severity: 'error', summary: `Unkown Error, status code: ${err?.response?.status}`, life: 5000 });
          }
        } else {
          toast.current.show({ severity: 'error', summary: 'Unkown Error', life: 5000 });
        }
      }
    })

  const putAdjustMutation = useMutation(putAdjustOption => {
    return putAdjustRequest(putAdjustOption.companyId, putAdjustOption.deviceId, putAdjustOption.requestBody)
    },
      {
        onSuccess: (updatedDevice) => {
          toast.current.show({ severity: 'success', summary: 'Success', detail: 'Selected device has been updated with adjusted values', life: 3000 })
          queryClient.setQueryData(['list', 'devices'], staleDevices => {
          const newDevices = [...staleDevices]
          const idx = newDevices.findIndex(staleDevice => staleDevice.id === updatedDevice.id)
          if (idx >= 0) {
            updatedDevice.companyName = companyMap[updatedDevice.companyId].name || ''
            newDevices[idx] = updatedDevice
            return newDevices
          } else {
            toast.current.show({ severity: 'error', summary: 'Something went wrong, please try again later', life: 5000 });
          }
        })
        hideDeviceAdjustDialog()
        },
        onError: (err) => {
        if (axios.isAxiosError(err)) {
          if (err?.response?.status === StatusCodes.UNAUTHORIZED || err?.response?.status === StatusCodes.FORBIDDEN) {
            let countDownSecond = 3;
            toast.current.show({ severity: 'error', summary: `Session expired, going to logout in ${countDownSecond} seconds... Please login again`, life: 3000 });
            setInterval(() => {
              countDownSecond--;
              if (countDownSecond <= 0) {
                dispatch({
                  type: 'LOGOUT',
                })
              }
            }, 1000);
          } else if (err?.response?.status === StatusCodes.BAD_REQUEST) {
            toast.current.show({ severity: 'error', summary: err.response?.data?.message, life: 5000 });
          } else if (err?.response?.status === StatusCodes.INTERNAL_SERVER_ERROR) {
            toast.current.show({ severity: 'error', summary: 'Internal server error, please try again later', life: 5000 });
          } else {
            toast.current.show({ severity: 'error', summary: `Unkown Error, status code: ${err?.response?.status}`, life: 5000 });
          }
        } else {
          toast.current.show({ severity: 'error', summary: 'Unkown Error', life: 5000 });
        }
      }
      }
  )

  const putSensorPreferenceMutation = useMutation(putSensorPreferenceMutationOption => {
    return putSensorPreferenceRequest(
      putSensorPreferenceMutationOption.companyId,
      putSensorPreferenceMutationOption.deviceId,
      putSensorPreferenceMutationOption.requestBody)
    },
      {
        onSuccess: (updatedDevice) => {
          toast.current.show({ severity: 'success', summary: 'Success', detail: 'Selected device has been updated with adjusted sensor preference', life: 3000 })
          toast.current.show({ severity: 'success', summary: 'Screen update in about 15 minutes for device reconfiguration and data download', life: 5000 });
          queryClient.setQueryData(['list', 'devices'], staleDevices => {
          const newDevices = [...staleDevices]
          const idx = newDevices.findIndex(staleDevice => staleDevice.id === updatedDevice.id)
          if (idx >= 0) {
            updatedDevice.companyName = companyMap[updatedDevice.companyId].name || ''
            newDevices[idx] = updatedDevice
            return newDevices
          } else {
            toast.current.show({ severity: 'error', summary: 'Something went wrong, please try again later', life: 5000 });
          }
        })
        hideEditSensorPreferenceDialog()
        },
        onError: (err) => {
        if (axios.isAxiosError(err)) {
          if (err?.response?.status === StatusCodes.UNAUTHORIZED || err?.response?.status === StatusCodes.FORBIDDEN) {
            let countDownSecond = 3;
            toast.current.show({ severity: 'error', summary: `Session expired, going to logout in ${countDownSecond} seconds... Please login again`, life: 3000 });
            setInterval(() => {
              countDownSecond--;
              if (countDownSecond <= 0) {
                dispatch({
                  type: 'LOGOUT',
                })
              }
            }, 1000);
          } else if (err?.response?.status === StatusCodes.BAD_REQUEST) {
            toast.current.show({ severity: 'error', summary: err.response?.data?.message, life: 5000 });
          } else if (err?.response?.status === StatusCodes.INTERNAL_SERVER_ERROR) {
            toast.current.show({ severity: 'error', summary: 'Internal server error, please try again later', life: 5000 });
          } else {
            toast.current.show({ severity: 'error', summary: `Unkown Error, status code: ${err?.response?.status}`, life: 5000 });
          }
        } else {
          toast.current.show({ severity: 'error', summary: 'Unkown Error', life: 5000 });
        }
      }
      }
  )

  const putDefaultSensorPreferenceMutation = useMutation(putDefaultSensorPreferenceMutationOption => {
    return putDefaultSensorPreferenceRequest(
      putDefaultSensorPreferenceMutationOption.companyId,
      putDefaultSensorPreferenceMutationOption.deviceId)
    },
      {
        onSuccess: (updatedDevice) => {
          toast.current.show({ severity: 'success', summary: 'Success', detail: 'Selected device has been reseted to default sensor preference', life: 3000 })
          queryClient.setQueryData(['list', 'devices'], staleDevices => {
          const newDevices = [...staleDevices]
          const idx = newDevices.findIndex(staleDevice => staleDevice.id === updatedDevice.id)
          if (idx >= 0) {
            updatedDevice.companyName = companyMap[updatedDevice.companyId].name || ''
            newDevices[idx] = updatedDevice
            return newDevices
          } else {
            toast.current.show({ severity: 'error', summary: 'Something went wrong, please try again later', life: 5000 });
          }
        })
        hideEditSensorPreferenceDialog()
        },
        onError: (err) => {
        if (axios.isAxiosError(err)) {
          if (err?.response?.status === StatusCodes.UNAUTHORIZED || err?.response?.status === StatusCodes.FORBIDDEN) {
            let countDownSecond = 3;
            toast.current.show({ severity: 'error', summary: `Session expired, going to logout in ${countDownSecond} seconds... Please login again`, life: 3000 });
            setInterval(() => {
              countDownSecond--;
              if (countDownSecond <= 0) {
                dispatch({
                  type: 'LOGOUT',
                })
              }
            }, 1000);
          } else if (err?.response?.status === StatusCodes.BAD_REQUEST) {
            toast.current.show({ severity: 'error', summary: err.response?.data?.message, life: 5000 });
          } else if (err?.response?.status === StatusCodes.INTERNAL_SERVER_ERROR) {
            toast.current.show({ severity: 'error', summary: 'Internal server error, please try again later', life: 5000 });
          } else {
            toast.current.show({ severity: 'error', summary: `Unkown Error, status code: ${err?.response?.status}`, life: 5000 });
          }
        } else {
          toast.current.show({ severity: 'error', summary: 'Unkown Error', life: 5000 });
        }
      }
      }
  )

   const putProbeTypeMutation = useMutation(putProbeTypeMutationOption => {
    return putProbeTypeRequest(
      putProbeTypeMutationOption.companyId,
      putProbeTypeMutationOption.deviceId,
      putProbeTypeMutationOption.requestBody)
    },
      {
        onSuccess: (updatedDevice) => {
          toast.current.show({ severity: 'success', summary: 'Success', detail: 'Selected device has been updated with adjusted probe type', life: 3000 })
          toast.current.show({ severity: 'success', summary: 'Screen update in about 15 minutes for device reconfiguration and data download', life: 5000 });
          queryClient.setQueryData(['list', 'devices'], staleDevices => {
          const newDevices = [...staleDevices]
          const idx = newDevices.findIndex(staleDevice => staleDevice.id === updatedDevice.id)
          if (idx >= 0) {
            updatedDevice.companyName = companyMap[updatedDevice.companyId].name || ''
            newDevices[idx] = updatedDevice
            return newDevices
          } else {
            toast.current.show({ severity: 'error', summary: 'Something went wrong, please try again later', life: 5000 });
          }
        })
        hideEditProbeTypeDialog()
        },
        onError: (err) => {
        if (axios.isAxiosError(err)) {
          if (err?.response?.status === StatusCodes.UNAUTHORIZED || err?.response?.status === StatusCodes.FORBIDDEN) {
            let countDownSecond = 3;
            toast.current.show({ severity: 'error', summary: `Session expired, going to logout in ${countDownSecond} seconds... Please login again`, life: 3000 });
            setInterval(() => {
              countDownSecond--;
              if (countDownSecond <= 0) {
                dispatch({
                  type: 'LOGOUT',
                })
              }
            }, 1000);
          } else if (err?.response?.status === StatusCodes.BAD_REQUEST) {
            toast.current.show({ severity: 'error', summary: err.response?.data?.message, life: 5000 });
          } else if (err?.response?.status === StatusCodes.INTERNAL_SERVER_ERROR) {
            toast.current.show({ severity: 'error', summary: 'Internal server error, please try again later', life: 5000 });
          } else {
            toast.current.show({ severity: 'error', summary: `Unkown Error, status code: ${err?.response?.status}`, life: 5000 });
          }
        } else {
          toast.current.show({ severity: 'error', summary: 'Unkown Error', life: 5000 });
        }
      }
      }
  )

  const openCreateDeviceDialog = () => {
    setIsCreateDeviceMode(true)
    setFocusedDevice(emptyDevice)
    setSubmitted(false)
    setCreateOrEditDeviceDialogVisibility(true)
  }

  const hideCreateOrEditDeviceDialog = () => {
    setIsCreateDeviceMode(false)
    setSubmitted(false)
    setCreateOrEditDeviceDialogVisibility(false)
  }

  const hideDeleteDeviceDialog = () => {
    setSubmitted(false)
    setDeleteDeviceDialogVisibility(false)
  }

  const hideDeviceDetailDialog = () => {
    setDeviceDetailDialog(false)
  }

  const hideDeviceAdjustDialog = () => {
    setAdjustDeviceDialogVisibility(false)
  }

  const hideEditSensorPreferenceDialog = () => {
    setEditSensorPreferenceDialogVisibility(false)
  }

  const hideResetToDefaultSensorPreferenceDialog = () => {
    setresetToDeafultSensorPreferenceDialogVisibility(false)
  }

  const hideEditProbeTypeDialog = () => {
    setEditProbeTypeDialogVisibility(false)
  }

  const hideDeviceDownlinkDialog = () => {
    setDeviceDownlink(emptyDownlinkOption)
    setDeviceDownlinkDialog(false)
  }

  const createOrEditDevice = () => {
    const isValidDevice = validateDevice(focusedDevice)
    if (!isValidDevice) {
      toast.current.show({ severity: 'error', summary: 'Please fill in required fields before apply.', life: 4000 });
      return
    }
    if (isCreateDeviceMode) {
      const requestBody = { ...focusedDevice }
      if (!requestBody.companyId) delete requestBody.companyId
      createDeviceMutation.mutate(requestBody)
    } else {
      patchDeviceMutation.mutate({
        companyId: focusedDevice.originalCompanyId,
        deviceId: focusedDevice.id,
        requestBody: { ...focusedDevice }
      })
    }
  }

  const validateDevice = (device) => {
    if (
      device?.serialNumber && device?.hardwareSerial && device?.service && device?.companyId
    ) {
      return true
    } else {
      return false
    }
  }

  // const doDownlink = (pkg) => {
  //   if (pkg === 'pkg16') {
  //     // check probe types, could not be same
  //     if (deviceDownlink.pkg16.sensorAType === deviceDownlink.pkg16.sensorBType ||
  //       deviceDownlink.pkg16.sensorAType === deviceDownlink.pkg16.sensorCType ||
  //       deviceDownlink.pkg16.sensorBType === deviceDownlink.pkg16.sensorCType) {
  //       toast.current.show({ severity: 'error', summary: 'Failed', detail: 'Probe types could not be same.', life: 5000 })
  //       return
  //     }
  //   }
  //   console.log(`send ${focusedDevice.hardwareSerial} ${JSON.stringify(deviceDownlink[pkg])}`)
  //   const deviceService = new DeviceService()
  //   deviceService.downlink(focusedDevice, pkg, deviceDownlink[`${pkg}`], (encodedPayload) => {
  //     toast.current.show({ severity: 'info', summary: 'Info', detail: `Send Downlink to ${focusedDevice.hardwareSerial} msg: ${JSON.stringify(encodedPayload)}`, life: 10000 })
  //   }).then(data => {
  //     if (data.errorCode === 0) {
  //       toast.current.show({ severity: 'success', summary: 'Successful', detail: 'Send downlink success', life: 5000 })
  //     } else {
  //       toast.current.show({ severity: 'error', summary: 'Failed', detail: 'Send downlink failed', life: 5000 })
  //     }
  //   })
  // }

  const editDevice = (device) => {
    setFocusedDevice({ ...device, originalCompanyId: device.companyId })
    setCreateOrEditDeviceDialogVisibility(true)
  }

  const showDeviceDetail = (device) => {
    setFocusedDevice(device)
    setDeviceDetailDialog(true)
  }

   const showAdjustDeviceDialogVisibility = (device) => {
    setFocusedDevice({ ...device })
    setAdjustDeviceDialogVisibility(true)
    setAdjustValues(emptyAdjustValues)
  }

  const showSensorPreferenceDetail = (device) => {
    setFocusedDevice({ ...device })
    setEditSensorPreferenceDialogVisibility(true)
    setEditingSensorPreference({
      nh3: device?.sensorPreference?.nh3,
      co2: device?.sensorPreference?.co2,
      temperature: device?.sensorPreference?.temperature,
      humidity: device?.sensorPreference?.humidity,
      windSpeed: device?.sensorPreference?.windSpeed,
    })
  }

  const showProbeTypeDetail = (device) => {
    setFocusedDevice({ ...device })
    setEditProbeTypeDialogVisibility(true)
    setEditingProbeType(device?.probeTypes)
  }

  const validateAdjust = (adjustValues) => {
    let isMulAddRandomOperatorAllFillIn = true

    for (const probeType in adjustValues) {
      const sensorType = adjustValues[probeType]
      for (const keys in sensorType) {
        const isOperatorEmpty = R.isEmpty(sensorType[keys].operator)
        const isValueEmpty = R.isEmpty(sensorType[keys].value)

        /* 若選擇的 operator 為 Mul Add Random ，但未將四個輸入框填滿，則將其認證為無效的提交。 */
        if (sensorType[keys].operator === 'muladdrandom' && sensorType[keys].value.length !== 4) {
           isMulAddRandomOperatorAllFillIn = false
        }

        /* 若選擇的 operator 為 Mul Add Random 以外，且未填寫數值，則改為預選 Add 、並指定其值為0。 */
        if (sensorType[keys].operator !== 'muladdrandom' && isValueEmpty) {
          sensorType[keys].operator = 'add'
          sensorType[keys].value = 0
        }

        /**
         * 若連 operator 都未選擇，就全部預選為 Add 、並指定其值為0。
         * TODO: 之後拉完list api，要確認原始數值不能更動到
         */
        if (isOperatorEmpty && isValueEmpty) {
          sensorType[keys].operator = 'add'
          sensorType[keys].value = 0
        }
      }
    }

    if (isMulAddRandomOperatorAllFillIn) {
      return true
    } else {
      return false
    }
  }

  const adjustDevice = () => {
    const isValidAdjust = validateAdjust(adjustValues)
    if (!isValidAdjust) {
      toast.current.show({ severity: 'error', summary: 'Please fill in required fields before apply.', life: 4000 });
      return
    } else {
      putAdjustMutation.mutate({
        companyId: focusedDevice?.companyId,
        deviceId: focusedDevice?.id,
        requestBody: { ...adjustValues }
      })
    }
  }

  const editSensorPreference = () => {
    putSensorPreferenceMutation.mutate({
      companyId: focusedDevice?.companyId,
      deviceId: focusedDevice?.id,
      requestBody: { ...editingSensorPreference }
    })
  }

  const resetToDefaultSensorPreference = () => {
    setresetToDeafultSensorPreferenceDialogVisibility(true)
  }

  const editProbeType = () => {
    putProbeTypeMutation.mutate({
      companyId: focusedDevice?.companyId,
      deviceId: focusedDevice?.id,
      requestBody: [ ...editingProbeType ]
    })
  }

  // const showDeviceDownlink = (device) => {
  //   setFocusedDevice(device)
  //   const deviceService = new DeviceService()
  //   // NOTE: fetch device downlink info from server
  //   deviceService.getCurrentDeviceSetting(device).then(data => {
  //     if (data.errorCode === 0) {
  //       setDeviceDownlink({
  //         pkg5: { payload: data.pkg5.payload, time: data.pkg5.lora_time },
  //         pkg6: { payload: data.pkg6.payload, time: data.pkg6.lora_time },
  //         pkg7: { payload: data.pkg7.payload, time: data.pkg7.lora_time },
  //         pkg11: {
  //           regularMeasurementInterval: data.pkg7 ? data.pkg7.regular_measurement : emptyDownlinkOption.pkg11.regularMeasurementInterval, // 0 ~ 255 minute
  //           statusReportInterval: data.pkg7 ? data.pkg7.status_report_interval : emptyDownlinkOption.pkg11.statusReportInterval, // 0 ~ 65536 minute Big-Endian
  //           sleepN1: data.pkg7 ? data.pkg7.sleep_n1 : emptyDownlinkOption.pkg11.sleepN1, // 0 ~ 255
  //           sleepN2: data.pkg7 ? data.pkg7.sleep_n2 : emptyDownlinkOption.pkg11.sleepN2 // 0 ~ 255
  //         },
  //         pkg12: {
  //           nh3Threshold: data.pkg6 && data.pkg6.nh3_offset_threshold
  //         },
  //         pkg13: {
  //           temperatureThreshold: data.pkg6 && data.pkg6.temp_offset_threshold
  //         },
  //         pkg14: {
  //           co2Threshold: data.pkg6 && data.pkg6.co2_offset_threshold
  //         },
  //         pkg15: {
  //           anemometerThreshold: data.pkg6 && data.pkg6.anemometer_threshold
  //         },
  //         pkg16: {
  //           sensorAType: data.pkg5 && data.pkg5.probe_a_type, // 0 ~ 255
  //           sensorBType: data.pkg5 && data.pkg5.probe_b_type, // 0 ~ 255
  //           sensorCType: data.pkg5 && data.pkg5.probe_c_type // 0 ~ 255
  //         }
  //       })
  //     } else {
  //       setDeviceDownlink({ ...emptyDownlinkOption })
  //     }

  //     setDeviceDownlinkDialog(true)
  //   })
  // }

  // const findIndexById = (id) => {
  //   let index = -1
  //   for (let i = 0; i < devices.length; i++) {
  //     if (devices[i].id === id) {
  //       index = i
  //       break
  //     }
  //   }

  //   return index
  // }

  const exportCSV = () => {
    dt.current.exportCSV()
  }

  const onInputChange = (e, name) => {
    const val = (e.target && e.target.value) || ''
    const _device = { ...focusedDevice }
    _device[`${name}`] = val

    setFocusedDevice(_device)
  }

  const operators = [
    { label: 'Add' , value: 'add' },
    { label: 'Multiple', value: 'mul' },
    { label: 'Divide', value: 'truediv' },
    { label: 'Mul Add Random', value: 'muladdrandom' },
    { label: 'Humidity Transform', value: 'hmodify' }
  ]

  const mulAddRandomOperationExample =
  `假設初始值為10,輸入陣列[3, 5, 1, 2]後：
   1. 在5和1之間隨機選擇一個數,假設選擇3.14。
   2. 將尾數作為10的指數次方,即10^2 = 100。
   3. 將前兩者相乘後四捨五入,再除以後者,即(3.14*100)/100 = 3.14。
   4. 將初始值乘以首數、加上上步驟的數,即可得: (10*3)+3.14 = 33.14。
  `
  const onInputOperatorChange = (e, selectedProbeType, selectedSensorType, operator) => {
    const val = (e.target && e.target.value) || ''
    const _adjustValues = { ...adjustValues }

    _adjustValues[selectedProbeType][selectedSensorType].operator = val
    /**
     * 此段邏輯是在處理，當使用者選了某個 operator 、輸完數值後，欲選其他 operator 時，須將該值清空。
     * 而除了 Mul Add Random 是以陣列儲存數值以外，其餘皆是單純輸入字串後轉為數字。
     */
    if (val === 'muladdrandom') {
       _adjustValues[selectedProbeType][selectedSensorType].value = ['']
    } else {
      _adjustValues[selectedProbeType][selectedSensorType].value = ''
    }

    setAdjustValues(_adjustValues)
  }

  const onInputValueChange = (e, selectedProbeType, selectedSensorType, value) => {
    const val = (e.target && e.target.value) || ''
    const _adjustValues = { ...adjustValues }

    /* 若選擇的 operator 為 Humidity Transform ，其輸入的數值僅能為`正整數` */
    if (_adjustValues[selectedProbeType][selectedSensorType].operator === 'hmodify') {

    const numVal = Number(val)
    if (numVal > 0 && Number.isInteger(numVal)) {
      _adjustValues[selectedProbeType][selectedSensorType].value = numVal
    } else {
      toast.current.show({ severity: 'error', summary: 'Please fill in the right value.', life: 4000 })
    }

    /* 若選擇的 operator 為 Mul Add Random ，其輸入的數值僅能為`正`的`整數`或`(僅到小數點後一位的)小數` */
    } else if (_adjustValues[selectedProbeType][selectedSensorType].operator === 'muladdrandom') {

      const numVal = Number(val)
      if (numVal > 0 && Math.round(numVal * 10) / 10 === numVal) {
        if(value === 'arr[0]') {
          _adjustValues[selectedProbeType][selectedSensorType].value[0] = numVal
        } else if (value === 'arr[1]') {
          _adjustValues[selectedProbeType][selectedSensorType].value[1] = numVal
        } else if (value === 'arr[2]') {
          _adjustValues[selectedProbeType][selectedSensorType].value[2] = numVal
        } else if (value === 'arr[3]') {
          _adjustValues[selectedProbeType][selectedSensorType].value[3] = numVal
        }
      } else {
        toast.current.show({ severity: 'error', summary: 'Please fill in the right value.', life: 4000 })
      }

    /* 若選擇的 operator 為 Add 、 Multiple 、 Divide ，其輸入的數值僅能為`整數` */
    } else if (_adjustValues[selectedProbeType][selectedSensorType].operator === 'add' ||
         _adjustValues[selectedProbeType][selectedSensorType].operator === 'mul' ||
         _adjustValues[selectedProbeType][selectedSensorType].operator === 'truediv') {

       if (val === '-') {
            _adjustValues[selectedProbeType][selectedSensorType].value = val
        } else {
            const numVal = Number(val)
            if (Number.isInteger(numVal)) {
                _adjustValues[selectedProbeType][selectedSensorType].value = numVal
            } else {
                toast.current.show({ severity: 'error', summary: 'Please fill in the right value.', life: 4000 })
            }
        }
    }

    setAdjustValues(_adjustValues)
  }

  const onInputSensorChange = (e, sensor) => {
    const val = (e.target && e.target.value) || ''
    const _editingSensorPreference = { ...editingSensorPreference }
    _editingSensorPreference[sensor] = val

    setEditingSensorPreference(_editingSensorPreference)
  }

  const onInputProbeChange = (e, idx) => {
    const val = (e.target && e.target.value) || ''
    const _editingProbeType = [ ...editingProbeType ]
    _editingProbeType[idx] = val

    setEditingProbeType(_editingProbeType)
  }

  const changeDownlinkFields = (pkg, field, val) => {
    const _downlink = { ...deviceDownlink }
    _downlink[`${pkg}`][`${field}`] = val
    setDeviceDownlink(_downlink)
  }

  const onDownlinkInputChange = (e, pkg, name, min, max) => {
    const val = e.target.value === '' ? 0 : parseInt(e.target.value)
    if (!isNaN(val)) {
      if (val > max) {
        changeDownlinkFields(pkg, name, max)
      } else if (val < min) {
        changeDownlinkFields(pkg, name, min)
      } else {
        changeDownlinkFields(pkg, name, val)
      }
    }
  }

  const onSliderValue = (e, pkg, name) => {
    const val = parseInt(e.value)
    if (!isNaN(val)) {
      changeDownlinkFields(pkg, name, val)
    }
  }

  const unbindDevice = (device) => {
    setFocusedDevice({ ...device })
    setDeleteDeviceDialogVisibility(true)
  }

  const confirmUnbindDevice = () => {
    const requestBody = { ...focusedDevice }
    const unbindDeviceOption = {
      positionId: requestBody.positionId,
      deviceId: requestBody.id
    }
    unbindDeviceMutation.mutate(unbindDeviceOption)
  }

  const confirmResetToDefaultSensorPreference = () => {
    putDefaultSensorPreferenceMutation.mutate({
      companyId: focusedDevice?.companyId,
      deviceId: focusedDevice?.id
    })
  }

  const leftToolbarTemplate = () => {
    return (
      <>
        <div className='my-2'>
          <Button label='New' icon='pi pi-plus' className='p-button-success mr-2' data-test-id='createNewDevice' onClick={openCreateDeviceDialog} />
        </div>
      </>
    )
  }

  const rightToolbarTemplate = () => {
    return (
      <>
        <FileUpload mode='basic' accept='image/*' maxFileSize={1000000} label='Import' chooseLabel='Import' className='mr-2 inline-block' />
        <Button label='Export' icon='pi pi-upload' className='p-button-help' onClick={exportCSV} />
      </>
    )
  }

  const serialNumberBodyTemplate = (rowData) => {
    const { companyName, businessName, farmName, houseName, positionName, positionId } = rowData;

    return (
      <>
        <span className='p-column-title'>Serial Number</span>
        <div className="flex flex-column gap-3">
          {/* Location */}
          <div className="flex flex-wrap align-items-center gap-1 text-sm text-gray-500">
            {[
              companyName,
              businessName,
              farmName,
              houseName,
              positionName,
            ].filter((name) => name != null).map((name, index) => (
              <span className="flex align-items-center white-space-nowrap gap-1" key={index}>
                {index !== 0 ? <i className="pi pi-angle-right" /> : null}
                <span>{name}</span>
              </span>
            ))}

            {positionId == null && (
              <span className="ml-1">
                <InfoEntry icon="pi pi-exclamation-circle" severity="warning">Unbound</InfoEntry>
              </span>
            )}
          </div>

          {/* Serial Number */}
          <div>
            <code className="text-lg">{rowData.serialNumber}</code>
          </div>

          {/* Hardware/Firmware Version */}
          <div className="flex gap-1">
            {rowData.hardwareVersion != null && (
              <Tag
                severity="danger"
                value={`hw: v${rowData.hardwareVersion}`}
              />
            )}
            {rowData.firmwareVersion != null && (
              <Tag
                severity="info"
                value={`fw: v${rowData.firmwareVersion}`}
              />
            )}
          </div>
        </div>
      </>
    )
  }

  const remarkBodyTemplate = (rowData) => {
    return (
      <>
        <span className='p-column-title'>Remark</span>
        {rowData.remark}
      </>
    )
  }

  const companyBodyTemplate = (rowData) => {
    return (
      <>
        <span className='p-column-title'>Company</span>
        {rowData.companyName || ''}
      </>
    )
  }

  const serviceBodyTemplate = (rowData) => {
    return (
      <>
        <span className='p-column-title'>Service</span>
        {rowData.service}
      </>
    )
  }

  const hardwareSerialBodyTemplate = (rowData) => {
    return (
      <>
        <span className='p-column-title'>Hardware Serial</span>
        <code>{rowData.hardwareSerial}</code>
      </>
    )
  }

  const probeTypesBodyTemplate = (rowData) => {
    const { probeTypes } = rowData;

    return (
      <>
        <span className='p-column-title'>Probe Types</span>
        {probeTypes != null ? (
          <ul>
            {probeTypes.map((probeType, probeIndex) => (
              <li
                key={probeIndex}
                className={probeType === 'INVALID' ? "text-gray-400" : ""}
              >
                <code>{probeType}</code>
              </li>
            ))}
          </ul>
        ) : null}
      </>
    )
  }

  const sensorPreferenceBodyTemplate = (rowData) => {
    const { sensorPreference } = rowData;

    return (
      <>
        <span className='p-column-title'>Sensor Preference</span>
        {sensorPreference != null ? (
          <div className="flex flex-column align-items-start gap-2">
            {[
              { key: 'nh3', name: 'NH₃' },
              { key: 'co2', name: 'CO₂' },
              { key: 'temperature', name: 'Temperature' },
              { key: 'humidity', name: 'Humidity' },
              { key: 'windSpeed', name: 'Wind Speed' },
            ].map(({ key, name }) => {
              const probeType = sensorPreference[key] ?? 'INVALID';

              return (
                <InfoEntry
                  key={key}
                  severity={probeType !== 'INVALID' ? 'info' : 'secondary'}
                >
                  {name}: <code>{probeType}</code>
                </InfoEntry>
              );
            })}
          </div>
        ) : null}
      </>
    )
  }

  const positionBodyTemplate = (rowData) => {
    return (
      <>
        <span className='p-column-title'>Position</span>
        <code>{rowData.positionId}</code>
      </>
    )
  }

  const actionBodyTemplate = (rowData) => {
    return (
      <div className='actions flex flex-wrap gap-2'>
        <Button tooltip='detail' icon='pi pi-info-circle' className='p-button-rounded p-button-info mt-2' data-test-id='showDeviceDetail' onClick={() => showDeviceDetail(rowData)} />
        <Button tooltip='edit' icon='pi pi-pencil' className='p-button-rounded p-button-success mt-2' onClick={() => editDevice(rowData)} />
        {/* <Button tooltip='adjust' icon='pi pi-bolt' className='p-button-rounded p-button-secondary mt-2' onClick={() => showAdjustDeviceDialogVisibility(rowData)} /> */}
        {/* <Button tooltip='sensor' icon='pi pi-sync' className='p-button-rounded p-button-warning mt-2' onClick={() => showSensorPreferenceDetail(rowData)} /> */}
        {/* <Button tooltip='probe' icon='pi pi-sync' className='p-button-rounded p-button-help mt-2' onClick={() => showProbeTypeDetail(rowData)} /> */}
        <Button tooltip='unbind device with position' icon='pi pi-link' className='p-button-rounded p-button-danger mt-2' data-test-id='unbindDevice' onClick={() => unbindDevice(rowData)} disabled={rowData.positionId == null}/>
        {/* TODO: 下一個階段串上+測試 */}
        {/* {rowData.service === 'calyxsensingdatas'
          ? <Button icon='pi pi-link' className='p-button-rounded p-button-warn mt-3' onClick={() => showDeviceDownlink(rowData)} />
          : <div />} */}
      </div>
    )
  }

  const connectionBodyTemplate = (rowData) => {
    if (R.isNil(rowData.positionId)) {
      return (
        <div className='actions' />
      )
    } else {
      return (
        <div className='actions'>
          {/* <Button icon='pi pi-paperclip' className='p-button-rounded p-button-info mr-2' onClick={() => linkUnlinkDevice(rowData)} /> */}
          {/* <Button tooltip='unbind device with position' icon='pi pi-link' className='p-button-rounded p-button-danger mt-2' data-test-id='unbindDevice' onClick={() => unbindDevice(rowData)} /> */}
        </div>
      )
    }
  }

  const parseProbType = (typeInt) => {
    switch (typeInt) {
      case 1:
        return 'CALYX_NH3'
      case 2:
        return 'EC_NH3'
      case 3:
        return 'DYNA_CO2'
      case 4:
        return 'SHT30'
      case 5:
        return 'SCD4X_CO2'
      case 6:
        return 'CYCFS1'
      default:
        return 'Unknown'
    }
  }

  const header = (
    <div className='flex flex-column md:flex-row md:justify-content-between md:align-items-center'>
      <h5 className='m-0'>Manage Calyx Devices</h5>
      <div className="flex flex-wrap gap-3">
        <Button
          className={classNames({ "hidden": selectedDevices.length === 0 })}
          label={`${selectedDevices.length} selected device(s)`}
          icon="pi pi-copy"
          severity="info"
          outlined
          tooltip="Copy Serial Number(s) to Clipboard"
          tooltipOptions={{ position: "top" }}
          onClick={copySelectedDeviceListToClipBoard}
        />
        <IconField iconPosition="left" className='block mt-2 md:mt-0'>
          <InputIcon className='pi pi-search' />
          <InputText type='search' data-test-id='searchDevice' onInput={(e) => setGlobalFilter(e.target.value)} placeholder='Search...' />
        </IconField>
      </div>
    </div>
  )

  const createOrEditdeviceDialogFooter = (
    <>
      <Button label='Cancel' icon='pi pi-times' className='p-button-text' onClick={hideCreateOrEditDeviceDialog} />
      <Button label='Save' icon='pi pi-check' className='p-button-text' data-test-id='savelNewDevice' onClick={createOrEditDevice} />
    </>
  )

  const deleteDeviceDialogFooter = (
    <>
      <Button label='Cancel' icon='pi pi-times' className='p-button-text' onClick={hideDeleteDeviceDialog} />
      <Button label='Save' icon='pi pi-check' className='p-button-text' data-test-id='unbindDevice' onClick={confirmUnbindDevice} />
    </>
  )

  const adjustDeviceDialogFooter = (
    <>
      <Button label='Cancel' icon='pi pi-times' className='p-button-text' onClick={hideDeviceAdjustDialog} />
      <Button label='Save' icon='pi pi-check' className='p-button-text' data-test-id='adjustDevice' onClick={adjustDevice} />
    </>
  )

  const editSensorPreferenceDialogFooter =  (
    <>
      <Button label='Cancel' icon='pi pi-times' className='p-button-text' onClick={hideEditSensorPreferenceDialog} />
      <Button label='Save' icon='pi pi-check' className='p-button-text' data-test-id='editSensorPreference' onClick={editSensorPreference}/>
      <Button label='Default' icon='pi pi-history' className='p-button-text' data-test-id='defaultSensorPreference' onClick={device => resetToDefaultSensorPreference(device)} />
    </>
  )

  const resetToDefaultSensorPreferenceDialogFooter = (
    <>
      <Button label='Cancel' icon='pi pi-times' className='p-button-text' onClick={hideResetToDefaultSensorPreferenceDialog} />
      <Button label='Save' icon='pi pi-check' className='p-button-text' data-test-id='resetDefaultSensorPreference' onClick={confirmResetToDefaultSensorPreference} />
    </>
  )

  const editProbeTypeDialogFooter =  (
    <>
      <Button label='Cancel' icon='pi pi-times' className='p-button-text' onClick={hideEditProbeTypeDialog} />
      <Button label='Save' icon='pi pi-check' className='p-button-text' data-test-id='editProbeType' onClick={editProbeType}/>
    </>
  )

  const formatUnixTime = (unixTime) => {
    return formatUTCTime(new Date(unixTime * 1000))
  }

  const allProbeTypes = ['calyx_x', 'ec_nh3', 'dyna_co2', 'sht30', 'scd4x_co2', 'cycfs1']
  const sensorProbeTypes = {
    nh3: ['calyx_x', 'ec_nh3'],
    co2: ['dyna_co2', 'scd4x_co2'],
    temperature: ['calyx_x', 'sht30', 'scd4x_co2'],
    humidity: ['calyx_x', 'sht30', 'scd4x_co2'],
    windSpeed: ['cycfs1']
  }

  const deviceProbeTypes = focusedDevice?.probeTypes
  const sensorPreference = focusedDevice?.sensorPreference

  function ShowSensorPreference () {
    const sensorElements = []

    for (let sensor in sensorPreference) {
      if (!R.isNil(sensorPreference[sensor])) {
        /**
         * 先由 sensorProbeTypes 對照出實際有的感測屬性下，有哪些探頭提供該感測屬性，
         * 接著再去看裝置實際有哪些探頭，然後將兩者取交集，即為使用者實際上能切換的探頭。
         * 而若是兩者交集只有一個，則無法讓使用者切換、直接顯示字串於畫面上。
         */
        const uniqueProbeTypes = sensorProbeTypes[sensor]
        const actualProbeTypes = new Set(deviceProbeTypes)
        const mutualProbeTypes = uniqueProbeTypes.filter(uniqueProbeType => actualProbeTypes.has(uniqueProbeType))

        sensorElements.push(
          <li className='p-2' key={sensor} >
            <label>{sensor}: </label>
            {mutualProbeTypes.length > 1
              ? <Dropdown style={{ width: '250px' }} value={editingSensorPreference[sensor]} options={mutualProbeTypes} onChange={(e) => onInputSensorChange(e, sensor)}/>
              : <span>{sensorPreference[sensor]}</span>}
          </li>
        )
      }
    }

    return (
      <ul>{sensorElements}</ul>
    )
  }

  function ShowProbeType () {
    return (
      editingProbeType?.map((currentProbeType, idx) => {
        /**
         * 先由 editingProbeType 過濾掉裝置中正在跑迴圈的探頭，得到本次下拉選單中不能選擇的探頭。
         * 接著再看我們有出產哪些探頭，將其刪去前述不能選擇的探頭，即為使用者實際上能切換的探頭。
         */
        const unselectableProbeTypes = new Set(editingProbeType.filter(probeType => probeType !== currentProbeType))
        const selectableProbeTypes = allProbeTypes.filter(probeType => !unselectableProbeTypes.has(probeType))

        return <div className='field' key={currentProbeType}>
          <label htmlFor='remark' style={{ color: 'gray', fontSize: '16px', marginRight: '10px' }}>Probe {idx+1}:</label>
          <ul>
            <li className='p-2'>
              <Dropdown style={{ width: '250px' }} placeholder={currentProbeType === 'INVALID' && 'INVALID'}
                value={editingProbeType[idx]} options={selectableProbeTypes} onChange={(e) => onInputProbeChange(e, idx)}/>
            </li>
          </ul>
        </div>
      }) || <code>empty</code>
    )
  }

  async function copySelectedDeviceListToClipBoard() {
    const csv = selectedDevices.map((device) => device.serialNumber).join(',');

    await navigator.clipboard.writeText(csv);
  }

  return (
    <div className='grid crud-demo'>
      <div className='col-12'>
        <div className='card'>
          <Toast ref={toast} />
          <Toolbar className='mb-4' left={leftToolbarTemplate} right={rightToolbarTemplate} />

          <div className='flex flex-wrap gap-2 mb-4'>
            <DeviceDownlinkButton />
          </div>

          {allCompaniesQuery.isFetching || allDevicesQuery.isFetching ?
            (
              <DataTable value={Array.from({ length: 10 })} className="p-datatable-striped company-table-skeleton" header={header}>
                <Column selectionMode='multiple' headerStyle={{ width: '0' }} />
                <Column field="serialNumber" header="Serial Number" style={{ width: '25%', minWidth: '10rem' }} body={<Skeleton />}></Column>
                <Column header='Hardware Serial' field='hardwareSerial' style={{ width: '15%', minWidth: '10rem' }} body={<Skeleton />}></Column>
                <Column header='Probe Types' style={{ width: '20%', minWidth: '10rem' }}  body={<Skeleton />}></Column>
                <Column header='Sensor Preference' style={{ width: '20%', minWidth: '10rem' }}  body={<Skeleton />}></Column>
                {/* <Column header='Company' field='company' style={{ width: '14.28%', minWidth: '10rem' }} body={<Skeleton />}></Column> */}
                {/* <Column header='Service' field='service' style={{ width: '14.28%', minWidth: '10rem' }} body={<Skeleton />}></Column> */}
                {/* <Column header='Position' field='positionId' style={{ width: '14.28%', minWidth: '10rem' }} body={<Skeleton />}></Column> */}
                {/* <Column header='Unbind' style={{ width: '14.28%', minWidth: '10rem' }} body={<Skeleton />}></Column> */}
                <Column header='Action' style={{ width: '20%', minWidth: '10rem' }} body={<Skeleton />}></Column>
              </DataTable>
            )
            :
            (
              <DataTable
                ref={dt} value={allDevices} selectionMode='checkbox' selection={selectedDevices} onSelectionChange={(e) => setSelectedDevices(e.value)}
                dataKey='id' paginator rows={50} rowsPerPageOptions={[5, 10, 25, 50, 100]} className='datatable-responsive'
                paginatorTemplate='FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink CurrentPageReport RowsPerPageDropdown'
                currentPageReportTemplate='Showing {first} to {last} of {totalRecords} devices'
                globalFilter={globalFilter}
                globalFilterFields={['serialNumber', 'companyName', 'businessName', 'farmName', 'hardwareSerial']}
                emptyMessage='No devices found.' header={header} responsiveLayout='scroll'
              >
                <Column selectionMode='multiple' headerStyle={{ width: '0' }}></Column>
                <Column header='Serial Number' field='serialNumber' sortable body={serialNumberBodyTemplate} headerStyle={{ width: '25%', minWidth: '10rem' }} />
                <Column header='Hardware Serial' field='hardwareSerial' sortable body={hardwareSerialBodyTemplate} headerStyle={{ width: '15%', minWidth: '10rem' }} />
                <Column header='Probe Types' body={probeTypesBodyTemplate} headerStyle={{ width: '20%', minWidth: '10rem' }} />
                <Column header='Sensor Preference' body={sensorPreferenceBodyTemplate} headerStyle={{ width: '20%', minWidth: '10rem' }} />
                {/* <Column header='Remark' field='remark' sortable body={remarkBodyTemplate} headerStyle={{ width: '14%', minWidth: '10rem' }} /> */}
                {/* <Column header='Company' field='companyName' sortable body={companyBodyTemplate} headerStyle={{ width: '14.28%', minWidth: '10rem' }} /> */}
                {/* <Column header='Service' field='service' sortable body={serviceBodyTemplate} headerStyle={{ width: '14.28%', minWidth: '10rem' }} /> */}
                {/* <Column header='Position' field='positionId' body={positionBodyTemplate} headerStyle={{ width: '14.28%', minWidth: '10rem' }} /> */}
                {/* <Column header='Unbind' body={connectionBodyTemplate} headerStyle={{ width: '14.28%', minWidth: '10rem' }} /> */}
                <Column header='Action' body={actionBodyTemplate} headerStyle={{ width: '20%', minWidth: '10rem' }} />
              </DataTable>
            )
          }

          <Dialog blockScroll={true} visible={createOrEditDeviceDialogVisibility} style={{ width: '450px' }} header={isCreateDeviceMode ? 'Create new device' : 'Edit device'} modal className='p-fluid' footer={createOrEditdeviceDialogFooter} onHide={hideCreateOrEditDeviceDialog}>

            <div className='field'>
              <label htmlFor='serialNumber'>Serial Number</label>
              <InputText
                id='serialNumber' data-test-id='typeSerialNumber' value={focusedDevice.serialNumber} onChange={(e) => onInputChange(e, 'serialNumber')} required autoFocus
                disabled={focusedDevice.id || createDeviceMutation.isLoading || patchDeviceMutation.isLoading}
                className={classNames({ 'p-invalid': submitted && !focusedDevice.serialNumber })}
              />
              {!focusedDevice.serialNumber && <small className='p-invalid' style={{ color: 'red' }} >Serial Number is required.</small>}
            </div>

            <div className='field'>
              <label htmlFor='hardwareSerial'>Hardware Serial</label>
              <InputText
                id='hardwareSerial' data-test-id='typeHardwareSerial'
                value={focusedDevice.hardwareSerial}
                onChange={(e) => onInputChange(e, 'hardwareSerial')}
                required autoFocus
                disabled={createDeviceMutation.isLoading || patchDeviceMutation.isLoading}
              />
              {!focusedDevice.hardwareSerial && <small className='p-invalid' style={{ color: 'red' }} >Hardware Serial is required.</small>}
            </div>

            {/*
            <div className='field'>
              <label htmlFor='name'>Name</label>
              <InputText id='name' value={device.name} onChange={(e) => onInputChange(e, 'name')} required autoFocus className={classNames({ 'p-invalid': submitted && !device.name })} />
              {submitted && !device.name && <small className='p-invalid'>Name is required.</small>}
            </div>
            */}

            <div className='field' data-test-id='chooseService'>
              <label htmlFor='service'>Service</label>
              <Dropdown
                id='dropdown' value={focusedDevice.service} options={deviceServicesDropdown} onChange={(e) => onInputChange(e, 'service')}
                required autoFocus
                disabled={createDeviceMutation.isLoading || patchDeviceMutation.isLoading}
              />
              {!focusedDevice.service && <small className='p-invalid'>Service is required.</small>}
            </div>

            <div className='field' data-test-id='chooseDeviceCompany'>
              <label htmlFor='company'>Company</label>
              <Dropdown
                id='company' value={focusedDevice.companyId} options={companyDropdown} filter onChange={(e) => onInputChange(e, 'companyId')}
                required
                disabled={createDeviceMutation.isLoading || patchDeviceMutation.isLoading}
              />
              {!focusedDevice.companyId && <small className='p-invalid' style={{ color: 'red' }} >Company is required.</small>}
            </div>
          </Dialog>

          <Dialog blockScroll={true} visible={deviceDetailDialog} style={{ width: '450px' }} header='Device Detail' modal onHide={hideDeviceDetailDialog}>
            <div className='field'>
              <label htmlFor='serialNumber' style={{ color: 'gray', fontSize: '18px', marginRight: '10px' }} >Serial Number: </label><label>{focusedDevice.serialNumber}</label>
            </div>
            <div className='field'>
              <label htmlFor='hardwareSerial' style={{ color: 'gray', fontSize: '18px', marginRight: '10px' }} >Hardware Serial: </label><label>{focusedDevice.hardwareSerial}</label>
            </div>
            <div className='field'>
              <label htmlFor='mobile' style={{ color: 'gray', fontSize: '18px', marginRight: '10px' }} >Company: </label><label>{focusedDevice.companyId}</label>
            </div>
            <div className='field'>
              <label htmlFor='service' style={{ color: 'gray', fontSize: '18px', marginRight: '10px' }} >Service: </label><label>{focusedDevice.service}</label>
            </div>
            <div className='field'>
              <label htmlFor='mobile' style={{ color: 'gray', fontSize: '18px', marginRight: '10px' }} >Position Id: </label><label data-test-id='checkPositionId'>{focusedDevice.positionId}</label>
            </div>
            {/* <div className='field'>
              <label htmlFor='remark' style={{ color: 'gray', fontSize: '18px', marginRight: '10px' }} >Remark: </label><label>{focusedDevice.remark}</label>
            </div> */}
            {focusedDevice?.service === 'calyxsensingdatas' ?
              <div className='field'>
                <label htmlFor='remark' style={{ color: 'gray', fontSize: '18px', marginRight: '10px' }} >Probe Type: </label>
                <ul>
                  {focusedDevice?.probeTypes?.map(probeType => <li key={probeType} >{probeType}</li>) ||
                    <code>empty</code>
                  }
                </ul>
              </div> : null
            }
          </Dialog>

          <Dialog blockScroll={true} visible={deleteDeviceDialogVisibility} style={{ width: '450px' }} header={'Unbind device confirmation?'} modal className='p-fluid' footer={deleteDeviceDialogFooter} onHide={hideDeleteDeviceDialog}>
          </Dialog>

          <Dialog visible={adjustDeviceDialogVisibility} style={{ width: '1000px' }} header={'Adjust Device Values'} modal className='p-fluid' footer={adjustDeviceDialogFooter} onHide={hideDeviceAdjustDialog}>
            <table className='w-full text-center'>
              <thead className='bg-bluegray-300'>
                <tr>
                  <th className='py-2'>Probe Type</th>
                  <th className='py-2'>Sensing Type</th>
                  <th className='py-2'>Operator</th>
                  <th className='py-2'>Values</th>
                </tr>
              </thead>
              <tbody>
                {/* calyx_x */}
                <tr className='bg-bluegray-50'>
                  <th rowSpan={3}>calyx_x</th>
                  <td>nh3</td>
                  <td>
                    <Dropdown value={adjustValues.calyx_x.nh3.operator} options={operators} onChange={(e) => onInputOperatorChange(e, 'calyx_x', 'nh3', 'operator')}/>
                  </td>
                  <td>
                    {adjustValues.calyx_x.nh3.operator
                      ? adjustValues.calyx_x.nh3.operator === 'muladdrandom'
                        ? <>
                            <InputText value={adjustValues.calyx_x.nh3.value[0]} onChange={(e) => onInputValueChange(e, 'calyx_x', 'nh3', 'arr[0]')} required
                              placeholder="Please enter a number greater than 0 with at most one decimal place." type="number"
                              tooltip={mulAddRandomOperationExample}/>
                            {!adjustValues.calyx_x.nh3.value[0] && <small className='p-invalid' style={{ color: 'red' }} >All elements are required.</small>}
                            <InputText value={adjustValues.calyx_x.nh3.value[1]} onChange={(e) => onInputValueChange(e, 'calyx_x', 'nh3', 'arr[1]')} required
                              placeholder="Please enter a number greater than 0 with at most one decimal place." type="number"/>
                            {!adjustValues.calyx_x.nh3.value[1] && <small className='p-invalid' style={{ color: 'red' }} >All elements are required.</small>}
                            <InputText value={adjustValues.calyx_x.nh3.value[2]} onChange={(e) => onInputValueChange(e, 'calyx_x', 'nh3', 'arr[2]')} required
                              placeholder="Please enter a number greater than 0 with at most one decimal place." type="number"/>
                            {!adjustValues.calyx_x.nh3.value[2] && <small className='p-invalid' style={{ color: 'red' }} >All elements are required.</small>}
                            <InputText value={adjustValues.calyx_x.nh3.value[3]} onChange={(e) => onInputValueChange(e, 'calyx_x', 'nh3', 'arr[3]')} required
                              placeholder="Please enter a number greater than 0 with at most one decimal place." type="number"/>
                            {!adjustValues.calyx_x.nh3.value[3] && <small className='p-invalid' style={{ color: 'red' }} >All elements are required.</small>}
                          </>
                        : adjustValues.calyx_x.nh3.operator === 'hmodify'
                          ? <InputText value={adjustValues.calyx_x.nh3.value} onChange={(e) => onInputValueChange(e, 'calyx_x', 'nh3', 'value')}
                              placeholder="please input an integer > 0" type="number"/>
                          : <InputText value={adjustValues.calyx_x.nh3.value} onChange={(e) => onInputValueChange(e, 'calyx_x', 'nh3', 'value')}
                              placeholder="please input an integer that can be > 0,  < 0, or = 0" type="number"/>
                      : ''
                    }
                  </td>
                </tr>
                <tr className='bg-bluegray-50'>
                  <td>temperature</td>
                  <td>
                    <Dropdown value={adjustValues.calyx_x.temperature.operator} options={operators} onChange={(e) => onInputOperatorChange(e, 'calyx_x', 'temperature', 'operator')}/>
                  </td>
                  <td>
                    {adjustValues.calyx_x.temperature.operator
                      ? adjustValues.calyx_x.temperature.operator === 'muladdrandom'
                        ?<>
                            <InputText value={adjustValues.calyx_x.temperature.value[0]} onChange={(e) => onInputValueChange(e, 'calyx_x', 'temperature', 'arr[0]')} required
                              placeholder="Please enter a number greater than 0 with at most one decimal place." type="number"
                              tooltip={mulAddRandomOperationExample}/>
                            {!adjustValues.calyx_x.temperature.value[0] && <small className='p-invalid' style={{ color: 'red' }} >All elements are required.</small>}
                            <InputText value={adjustValues.calyx_x.temperature.value[1]} onChange={(e) => onInputValueChange(e, 'calyx_x', 'temperature', 'arr[1]')} required
                              placeholder="Please enter a number greater than 0 with at most one decimal place." type="number"/>
                            {!adjustValues.calyx_x.temperature.value[1] && <small className='p-invalid' style={{ color: 'red' }} >All elements are required.</small>}
                            <InputText value={adjustValues.calyx_x.temperature.value[2]} onChange={(e) => onInputValueChange(e, 'calyx_x', 'temperature', 'arr[2]')} required
                              placeholder="Please enter a number greater than 0 with at most one decimal place." type="number"/>
                            {!adjustValues.calyx_x.temperature.value[2] && <small className='p-invalid' style={{ color: 'red' }} >All elements are required.</small>}
                            <InputText value={adjustValues.calyx_x.temperature.value[3]} onChange={(e) => onInputValueChange(e, 'calyx_x', 'temperature', 'arr[3]')} required
                              placeholder="Please enter a number greater than 0 with at most one decimal place." type="number"/>
                            {!adjustValues.calyx_x.temperature.value[3] && <small className='p-invalid' style={{ color: 'red' }} >All elements are required.</small>}
                          </>
                        : adjustValues.calyx_x.temperature.operator === 'hmodify'
                          ? <InputText value={adjustValues.calyx_x.temperature.value} onChange={(e) => onInputValueChange(e, 'calyx_x', 'temperature', 'value')}
                              placeholder="please input an integer > 0" type="number"/>
                          : <InputText value={adjustValues.calyx_x.temperature.value} onChange={(e) => onInputValueChange(e, 'calyx_x', 'temperature', 'value')}
                              placeholder="please input an integer that can be > 0,  < 0, or = 0" type="number"/>
                      : ''
                    }
                  </td>
                </tr>
                <tr className='bg-bluegray-50'>
                  <td>humidity</td>
                  <td>
                    <Dropdown value={adjustValues.calyx_x.humidity.operator} options={operators} onChange={(e) => onInputOperatorChange(e, 'calyx_x', 'humidity', 'operator')}/>
                  </td>
                  <td>
                    {adjustValues.calyx_x.humidity.operator
                      ? adjustValues.calyx_x.humidity.operator === 'muladdrandom'
                        ? <>
                            <InputText value={adjustValues.calyx_x.humidity.value[0]} onChange={(e) => onInputValueChange(e, 'calyx_x', 'humidity', 'arr[0]')} required
                              placeholder="Please enter a number greater than 0 with at most one decimal place." type="number"
                              tooltip={mulAddRandomOperationExample}/>
                            {!adjustValues.calyx_x.humidity.value[0] && <small className='p-invalid' style={{ color: 'red' }} >All elements are required.</small>}
                            <InputText value={adjustValues.calyx_x.humidity.value[1]} onChange={(e) => onInputValueChange(e, 'calyx_x', 'humidity', 'arr[1]')} required
                              placeholder="Please enter a number greater than 0 with at most one decimal place." type="number"/>
                              {!adjustValues.calyx_x.humidity.value[1] && <small className='p-invalid' style={{ color: 'red' }} >All elements are required.</small>}
                            <InputText value={adjustValues.calyx_x.humidity.value[2]} onChange={(e) => onInputValueChange(e, 'calyx_x', 'humidity', 'arr[2]')} required
                              placeholder="Please enter a number greater than 0 with at most one decimal place." type="number"/>
                              {!adjustValues.calyx_x.humidity.value[2] && <small className='p-invalid' style={{ color: 'red' }} >All elements are required.</small>}
                            <InputText value={adjustValues.calyx_x.humidity.value[3]} onChange={(e) => onInputValueChange(e, 'calyx_x', 'humidity', 'arr[3]')} required
                              placeholder="Please enter a number greater than 0 with at most one decimal place." type="number"/>
                              {!adjustValues.calyx_x.humidity.value[3] && <small className='p-invalid' style={{ color: 'red' }} >All elements are required.</small>}
                          </>
                        : adjustValues.calyx_x.humidity.operator === 'hmodify'
                          ? <InputText value={adjustValues.calyx_x.humidity.value} onChange={(e) => onInputValueChange(e, 'calyx_x', 'humidity', 'value')}
                              placeholder="please input an integer > 0" type="number"/>
                          : <InputText value={adjustValues.calyx_x.humidity.value} onChange={(e) => onInputValueChange(e, 'calyx_x', 'humidity', 'value')}
                              placeholder="please input an integer that can be > 0,  < 0, or = 0" type="number"/>
                      : ''
                    }
                  </td>
                </tr>
                {/* ec_nh3 */}
                <tr className='bg-bluegray-100'>
                  <th>ec_nh3</th>
                  <td>nh3</td>
                  <td>
                    <Dropdown value={adjustValues.ec_nh3.nh3.operator} options={operators} onChange={(e) => onInputOperatorChange(e, 'ec_nh3', 'nh3', 'operator')}/>
                  </td>
                  <td>
                    {adjustValues.ec_nh3.nh3.operator
                      ? adjustValues.ec_nh3.nh3.operator === 'muladdrandom'
                        ? <>
                            <InputText value={adjustValues.ec_nh3.nh3.value[0]} onChange={(e) => onInputValueChange(e, 'ec_nh3', 'nh3', 'arr[0]')} required
                              placeholder="Please enter a number greater than 0 with at most one decimal place." type="number"
                              tooltip={mulAddRandomOperationExample}/>
                            {!adjustValues.ec_nh3.nh3.value[0] && <small className='p-invalid' style={{ color: 'red' }} >All elements are required.</small>}
                            <InputText value={adjustValues.ec_nh3.nh3.value[1]} onChange={(e) => onInputValueChange(e, 'ec_nh3', 'nh3', 'arr[1]')} required
                              placeholder="Please enter a number greater than 0 with at most one decimal place." type="number"/>
                            {!adjustValues.ec_nh3.nh3.value[1] && <small className='p-invalid' style={{ color: 'red' }} >All elements are required.</small>}
                            <InputText value={adjustValues.ec_nh3.nh3.value[2]} onChange={(e) => onInputValueChange(e, 'ec_nh3', 'nh3', 'arr[2]')} required
                              placeholder="Please enter a number greater than 0 with at most one decimal place." type="number"/>
                            {!adjustValues.ec_nh3.nh3.value[2] && <small className='p-invalid' style={{ color: 'red' }} >All elements are required.</small>}
                            <InputText value={adjustValues.ec_nh3.nh3.value[3]} onChange={(e) => onInputValueChange(e, 'ec_nh3', 'nh3', 'arr[3]')} required
                              placeholder="Please enter a number greater than 0 with at most one decimal place." type="number"/>
                            {!adjustValues.ec_nh3.nh3.value[3] && <small className='p-invalid' style={{ color: 'red' }} >All elements are required.</small>}
                          </>
                        : adjustValues.ec_nh3.nh3.operator === 'hmodify'
                          ? <InputText value={adjustValues.ec_nh3.nh3.value} onChange={(e) => onInputValueChange(e, 'ec_nh3', 'nh3', 'value')}
                              placeholder="please input an integer > 0" type="number"/>
                          : <InputText value={adjustValues.ec_nh3.nh3.value} onChange={(e) => onInputValueChange(e, 'ec_nh3', 'nh3', 'value')}
                              placeholder="please input an integer that can be > 0,  < 0, or = 0" type="number"/>
                      : ''
                    }
                  </td>
                </tr>
                {/* dyna_co2 */}
                <tr className='bg-bluegray-50'>
                  <th>dyna_co2</th>
                  <td>co2</td>
                  <td>
                    <Dropdown value={adjustValues.dyna_co2.co2.operator} options={operators} onChange={(e) => onInputOperatorChange(e, 'dyna_co2', 'co2', 'operator')}/>
                  </td>
                  <td>
                    {adjustValues.dyna_co2.co2.operator
                      ? adjustValues.dyna_co2.co2.operator === 'muladdrandom'
                        ?  <>
                            <InputText value={adjustValues.dyna_co2.co2.value[0]} onChange={(e) => onInputValueChange(e, 'dyna_co2', 'co2', 'arr[0]')} required
                              placeholder="Please enter a number greater than 0 with at most one decimal place." type="number"
                              tooltip={mulAddRandomOperationExample}/>
                            {!adjustValues.dyna_co2.co2.value[0] && <small className='p-invalid' style={{ color: 'red' }} >All elements are required.</small>}
                            <InputText value={adjustValues.dyna_co2.co2.value[1]} onChange={(e) => onInputValueChange(e, 'dyna_co2', 'co2', 'arr[1]')} required
                              placeholder="Please enter a number greater than 0 with at most one decimal place." type="number"/>
                            {!adjustValues.dyna_co2.co2.value[1] && <small className='p-invalid' style={{ color: 'red' }} >All elements are required.</small>}
                            <InputText value={adjustValues.dyna_co2.co2.value[2]} onChange={(e) => onInputValueChange(e, 'dyna_co2', 'co2', 'arr[2]')} required
                              placeholder="Please enter a number greater than 0 with at most one decimal place." type="number"/>
                            {!adjustValues.dyna_co2.co2.value[2] && <small className='p-invalid' style={{ color: 'red' }} >All elements are required.</small>}
                            <InputText value={adjustValues.dyna_co2.co2.value[3]} onChange={(e) => onInputValueChange(e, 'dyna_co2', 'co2', 'arr[3]')} required
                              placeholder="Please enter a number greater than 0 with at most one decimal place." type="number"/>
                            {!adjustValues.dyna_co2.co2.value[3] && <small className='p-invalid' style={{ color: 'red' }} >All elements are required.</small>}
                          </>
                        : adjustValues.dyna_co2.co2.operator === 'hmodify'
                          ? <InputText value={adjustValues.dyna_co2.co2.value} onChange={(e) => onInputValueChange(e, 'dyna_co2', 'co2', 'value')}
                              placeholder="please input an integer > 0" type="number"/>
                          : <InputText value={adjustValues.dyna_co2.co2.value} onChange={(e) => onInputValueChange(e, 'dyna_co2', 'co2', 'value')}
                              placeholder="please input an integer that can be > 0,  < 0, or = 0" type="number"/>
                      : ''
                    }
                  </td>
                </tr>
                {/* sht30 */}
                <tr className='bg-bluegray-100'>
                  <th rowSpan={2}>sht30</th>
                  <td>temperature</td>
                  <td>
                    <Dropdown value={adjustValues.sht30.temperature.operator} options={operators} onChange={(e) => onInputOperatorChange(e, 'sht30', 'temperature', 'operator')}/>
                  </td>
                  <td>
                    {adjustValues.sht30.temperature.operator
                      ? adjustValues.sht30.temperature.operator === 'muladdrandom'
                        ?  <>
                            <InputText value={adjustValues.sht30.temperature.value[0]} onChange={(e) => onInputValueChange(e, 'sht30', 'temperature', 'arr[0]')} required
                              placeholder="Please enter a number greater than 0 with at most one decimal place." type="number"
                              tooltip={mulAddRandomOperationExample}/>
                            {!adjustValues.sht30.temperature.value[0] && <small className='p-invalid' style={{ color: 'red' }} >All elements are required.</small>}
                            <InputText value={adjustValues.sht30.temperature.value[1]} onChange={(e) => onInputValueChange(e, 'sht30', 'temperature', 'arr[1]')} required
                              placeholder="Please enter a number greater than 0 with at most one decimal place." type="number"/>
                            {!adjustValues.sht30.temperature.value[1] && <small className='p-invalid' style={{ color: 'red' }} >All elements are required.</small>}
                            <InputText value={adjustValues.sht30.temperature.value[2]} onChange={(e) => onInputValueChange(e, 'sht30', 'temperature', 'arr[2]')} required
                              placeholder="Please enter a number greater than 0 with at most one decimal place." type="number"/>
                            {!adjustValues.sht30.temperature.value[2] && <small className='p-invalid' style={{ color: 'red' }} >All elements are required.</small>}
                            <InputText value={adjustValues.sht30.temperature.value[3]} onChange={(e) => onInputValueChange(e, 'sht30', 'temperature', 'arr[3]')} required
                              placeholder="Please enter a number greater than 0 with at most one decimal place." type="number"/>
                            {!adjustValues.sht30.temperature.value[3] && <small className='p-invalid' style={{ color: 'red' }} >All elements are required.</small>}
                          </>
                        : adjustValues.sht30.temperature.operator === 'hmodify'
                          ? <InputText value={adjustValues.sht30.temperature.value} onChange={(e) => onInputValueChange(e, 'sht30', 'temperature', 'value')}
                              placeholder="please input an integer > 0" type="number"/>
                          : <InputText value={adjustValues.sht30.temperature.value} onChange={(e) => onInputValueChange(e, 'sht30', 'temperature', 'value')}
                              placeholder="please input an integer that can be > 0,  < 0, or = 0" type="number"/>
                      : ''
                    }
                  </td>
                </tr>
                <tr className='bg-bluegray-100'>
                  <td>humidity</td>
                  <td>
                    <Dropdown value={adjustValues.sht30.humidity.operator} options={operators} onChange={(e) => onInputOperatorChange(e, 'sht30', 'humidity', 'operator')}/>
                  </td>
                  <td>
                    {adjustValues.sht30.humidity.operator
                      ? adjustValues.sht30.humidity.operator === 'muladdrandom'
                        ? <>
                            <InputText value={adjustValues.sht30.humidity.value[0]} onChange={(e) => onInputValueChange(e, 'sht30', 'humidity', 'arr[0]')} required
                              placeholder="Please enter a number greater than 0 with at most one decimal place." type="number"
                              tooltip={mulAddRandomOperationExample}/>
                            {!adjustValues.sht30.humidity.value[0] && <small className='p-invalid' style={{ color: 'red' }} >All elements are required.</small>}
                            <InputText value={adjustValues.sht30.humidity.value[1]} onChange={(e) => onInputValueChange(e, 'sht30', 'humidity', 'arr[1]')} required
                              placeholder="Please enter a number greater than 0 with at most one decimal place." type="number"/>
                            {!adjustValues.sht30.humidity.value[1] && <small className='p-invalid' style={{ color: 'red' }} >All elements are required.</small>}
                            <InputText value={adjustValues.sht30.humidity.value[2]} onChange={(e) => onInputValueChange(e, 'sht30', 'humidity', 'arr[2]')} required
                              placeholder="Please enter a number greater than 0 with at most one decimal place." type="number"/>
                            {!adjustValues.sht30.humidity.value[2] && <small className='p-invalid' style={{ color: 'red' }} >All elements are required.</small>}
                            <InputText value={adjustValues.sht30.humidity.value[3]} onChange={(e) => onInputValueChange(e, 'sht30', 'humidity', 'arr[3]')} required
                              placeholder="Please enter a number greater than 0 with at most one decimal place." type="number"/>
                            {!adjustValues.sht30.humidity.value[3] && <small className='p-invalid' style={{ color: 'red' }} >All elements are required.</small>}
                          </>
                        : adjustValues.sht30.humidity.operator === 'hmodify'
                          ? <InputText value={adjustValues.sht30.humidity.value} onChange={(e) => onInputValueChange(e, 'sht30', 'humidity', 'value')}
                              placeholder="please input an integer > 0" type="number"/>
                          : <InputText value={adjustValues.sht30.humidity.value} onChange={(e) => onInputValueChange(e, 'sht30', 'humidity', 'value')}
                              placeholder="please input an integer that can be > 0,  < 0, or = 0" type="number"/>
                      : ''
                    }
                  </td>
                </tr>
                {/* scd4x_co2 */}
                <tr className='bg-bluegray-50'>
                  <th rowSpan={3}>scd4x_co2</th>
                  <td>co2</td>
                  <td>
                    <Dropdown value={adjustValues.scd4x_co2.co2.operator} options={operators} onChange={(e) => onInputOperatorChange(e, 'scd4x_co2', 'co2', 'operator')}/>
                  </td>
                  <td>
                    {adjustValues.scd4x_co2.co2.operator
                      ? adjustValues.scd4x_co2.co2.operator === 'muladdrandom'
                        ? <>
                            <InputText value={adjustValues.scd4x_co2.co2.value[0]} onChange={(e) => onInputValueChange(e, 'scd4x_co2', 'co2', 'arr[0]')} required
                              placeholder="Please enter a number greater than 0 with at most one decimal place." type="number"
                              tooltip={mulAddRandomOperationExample}/>
                            {!adjustValues.scd4x_co2.co2.value[0] && <small className='p-invalid' style={{ color: 'red' }} >All elements are required.</small>}
                            <InputText value={adjustValues.scd4x_co2.co2.value[1]} onChange={(e) => onInputValueChange(e, 'scd4x_co2', 'co2', 'arr[1]')} required
                              placeholder="Please enter a number greater than 0 with at most one decimal place." type="number"/>
                            {!adjustValues.scd4x_co2.co2.value[1] && <small className='p-invalid' style={{ color: 'red' }} >All elements are required.</small>}
                            <InputText value={adjustValues.scd4x_co2.co2.value[2]} onChange={(e) => onInputValueChange(e, 'scd4x_co2', 'co2', 'arr[2]')} required
                              placeholder="Please enter a number greater than 0 with at most one decimal place." type="number"/>
                            {!adjustValues.scd4x_co2.co2.value[2] && <small className='p-invalid' style={{ color: 'red' }} >All elements are required.</small>}
                            <InputText value={adjustValues.scd4x_co2.co2.value[3]} onChange={(e) => onInputValueChange(e, 'scd4x_co2', 'co2', 'arr[3]')} required
                              placeholder="Please enter a number greater than 0 with at most one decimal place." type="number"/>
                            {!adjustValues.scd4x_co2.co2.value[3] && <small className='p-invalid' style={{ color: 'red' }} >All elements are required.</small>}
                          </>
                        : adjustValues.scd4x_co2.co2.operator === 'hmodify'
                          ? <InputText value={adjustValues.scd4x_co2.co2.value} onChange={(e) => onInputValueChange(e, 'scd4x_co2', 'co2', 'value')}
                              placeholder="please input an integer > 0" type="number"/>
                          : <InputText value={adjustValues.scd4x_co2.co2.value} onChange={(e) => onInputValueChange(e, 'scd4x_co2', 'co2', 'value')}
                              placeholder="please input an integer that can be > 0,  < 0, or = 0" type="number"/>
                      : ''
                    }
                  </td>
                </tr>
                <tr className='bg-bluegray-50'>
                  <td>temperature</td>
                  <td>
                    <Dropdown value={adjustValues.scd4x_co2.temperature.operator} options={operators} onChange={(e) => onInputOperatorChange(e, 'scd4x_co2', 'temperature', 'operator')}/>
                  </td>
                  <td>
                    {adjustValues.scd4x_co2.temperature.operator
                      ? adjustValues.scd4x_co2.temperature.operator === 'muladdrandom'
                        ? <>
                            <InputText value={adjustValues.scd4x_co2.temperature.value[0]} onChange={(e) => onInputValueChange(e, 'scd4x_co2', 'temperature', 'arr[0]')} required
                              placeholder="Please enter a number greater than 0 with at most one decimal place." type="number"
                              tooltip={mulAddRandomOperationExample}/>
                            {!adjustValues.scd4x_co2.temperature.value[0] && <small className='p-invalid' style={{ color: 'red' }} >All elements are required.</small>}
                            <InputText value={adjustValues.scd4x_co2.temperature.value[1]} onChange={(e) => onInputValueChange(e, 'scd4x_co2', 'temperature', 'arr[1]')} required
                              placeholder="Please enter a number greater than 0 with at most one decimal place." type="number"/>
                            {!adjustValues.scd4x_co2.temperature.value[1] && <small className='p-invalid' style={{ color: 'red' }} >All elements are required.</small>}
                            <InputText value={adjustValues.scd4x_co2.temperature.value[2]} onChange={(e) => onInputValueChange(e, 'scd4x_co2', 'temperature', 'arr[2]')} required
                              placeholder="Please enter a number greater than 0 with at most one decimal place." type="number"/>
                            {!adjustValues.scd4x_co2.temperature.value[2] && <small className='p-invalid' style={{ color: 'red' }} >All elements are required.</small>}
                            <InputText value={adjustValues.scd4x_co2.temperature.value[3]} onChange={(e) => onInputValueChange(e, 'scd4x_co2', 'temperature', 'arr[3]')} required
                              placeholder="Please enter a number greater than 0 with at most one decimal place." type="number"/>
                            {!adjustValues.scd4x_co2.temperature.value[3] && <small className='p-invalid' style={{ color: 'red' }} >All elements are required.</small>}
                          </>
                        : adjustValues.scd4x_co2.temperature.operator === 'hmodify'
                          ? <InputText value={adjustValues.scd4x_co2.temperature.value} onChange={(e) => onInputValueChange(e, 'scd4x_co2', 'temperature', 'value')}
                              placeholder="please input an integer > 0" type="number"/>
                          : <InputText value={adjustValues.scd4x_co2.temperature.value} onChange={(e) => onInputValueChange(e, 'scd4x_co2', 'temperature', 'value')}
                              placeholder="please input an integer that can be > 0,  < 0, or = 0" type="number"/>
                      : ''
                    }
                  </td>
                </tr>
                <tr className='bg-bluegray-50'>
                  <td>humidity</td>
                  <td>
                    <Dropdown value={adjustValues.scd4x_co2.humidity.operator} options={operators} onChange={(e) => onInputOperatorChange(e, 'scd4x_co2', 'humidity', 'operator')}/>
                  </td>
                  <td>
                    {adjustValues.scd4x_co2.humidity.operator
                      ? adjustValues.scd4x_co2.humidity.operator === 'muladdrandom'
                        ? <>
                            <InputText value={adjustValues.scd4x_co2.humidity.value[0]} onChange={(e) => onInputValueChange(e, 'scd4x_co2', 'humidity', 'arr[0]')} required
                              placeholder="Please enter a number greater than 0 with at most one decimal place." type="number"
                              tooltip={mulAddRandomOperationExample}/>
                            {!adjustValues.scd4x_co2.humidity.value[0] && <small className='p-invalid' style={{ color: 'red' }} >All elements are required.</small>}
                            <InputText value={adjustValues.scd4x_co2.humidity.value[1]} onChange={(e) => onInputValueChange(e, 'scd4x_co2', 'humidity', 'arr[1]')} required
                              placeholder="Please enter a number greater than 0 with at most one decimal place." type="number"/>
                            {!adjustValues.scd4x_co2.humidity.value[1] && <small className='p-invalid' style={{ color: 'red' }} >All elements are required.</small>}
                            <InputText value={adjustValues.scd4x_co2.humidity.value[2]} onChange={(e) => onInputValueChange(e, 'scd4x_co2', 'humidity', 'arr[2]')} required
                              placeholder="Please enter a number greater than 0 with at most one decimal place." type="number"/>
                            {!adjustValues.scd4x_co2.humidity.value[2] && <small className='p-invalid' style={{ color: 'red' }} >All elements are required.</small>}
                            <InputText value={adjustValues.scd4x_co2.humidity.value[3]} onChange={(e) => onInputValueChange(e, 'scd4x_co2', 'humidity', 'arr[3]')} required
                              placeholder="Please enter a number greater than 0 with at most one decimal place." type="number"/>
                            {!adjustValues.scd4x_co2.humidity.value[3] && <small className='p-invalid' style={{ color: 'red' }} >All elements are required.</small>}
                          </>
                        : adjustValues.scd4x_co2.humidity.operator === 'hmodify'
                          ? <InputText value={adjustValues.scd4x_co2.humidity.value} onChange={(e) => onInputValueChange(e, 'scd4x_co2', 'humidity', 'value')}
                              placeholder="please input an integer > 0" type="number"/>
                          : <InputText value={adjustValues.scd4x_co2.humidity.value} onChange={(e) => onInputValueChange(e, 'scd4x_co2', 'humidity', 'value')}
                              placeholder="please input an integer that can be > 0,  < 0, or = 0" type="number"/>
                      : ''
                    }
                  </td>
                </tr>
                {/* cycfs1 */}
                <tr className='bg-bluegray-100'>
                  <th>cycfs1</th>
                  <td>wind speed</td>
                  <td>
                    <Dropdown value={adjustValues.cycfs1.wind_speed.operator} options={operators} onChange={(e) => onInputOperatorChange(e, 'cycfs1', 'wind_speed', 'operator')}/>
                  </td>
                  <td>
                    {adjustValues.cycfs1.wind_speed.operator
                      ? adjustValues.cycfs1.wind_speed.operator === 'muladdrandom'
                        ? <>
                            <InputText value={adjustValues.cycfs1.wind_speed.value[0]} onChange={(e) => onInputValueChange(e, 'cycfs1', 'wind_speed', 'arr[0]')} required
                              placeholder="Please enter a number greater than 0 with at most one decimal place." type="number"
                              tooltip={mulAddRandomOperationExample}/>
                            {!adjustValues.cycfs1.wind_speed.value[0] && <small className='p-invalid' style={{ color: 'red' }} >All elements are required.</small>}
                            <InputText value={adjustValues.cycfs1.wind_speed.value[1]} onChange={(e) => onInputValueChange(e, 'cycfs1', 'wind_speed', 'arr[1]')} required
                              placeholder="Please enter a number greater than 0 with at most one decimal place." type="number"/>
                            {!adjustValues.cycfs1.wind_speed.value[1] && <small className='p-invalid' style={{ color: 'red' }} >All elements are required.</small>}
                            <InputText value={adjustValues.cycfs1.wind_speed.value[2]} onChange={(e) => onInputValueChange(e, 'cycfs1', 'wind_speed', 'arr[2]')} required
                              placeholder="Please enter a number greater than 0 with at most one decimal place." type="number"/>
                            {!adjustValues.cycfs1.wind_speed.value[2] && <small className='p-invalid' style={{ color: 'red' }} >All elements are required.</small>}
                            <InputText value={adjustValues.cycfs1.wind_speed.value[3]} onChange={(e) => onInputValueChange(e, 'cycfs1', 'wind_speed', 'arr[3]')} required
                              placeholder="Please enter a number greater than 0 with at most one decimal place." type="number"/>
                            {!adjustValues.cycfs1.wind_speed.value[3] && <small className='p-invalid' style={{ color: 'red' }} >All elements are required.</small>}
                          </>
                        : adjustValues.cycfs1.wind_speed.operator === 'hmodify'
                          ? <InputText value={adjustValues.cycfs1.wind_speed.value} onChange={(e) => onInputValueChange(e, 'cycfs1', 'wind_speed', 'value')}
                              placeholder="please input an integer > 0" type="number"/>
                          : <InputText value={adjustValues.cycfs1.wind_speed.value} onChange={(e) => onInputValueChange(e, 'cycfs1', 'wind_speed', 'value')}
                              placeholder="please input an integer that can be > 0,  < 0, or = 0" type="number"/>
                      : ''
                    }
                  </td>
                </tr>
              </tbody>
            </table>
          </Dialog>

          <Dialog blockScroll={true} visible={editSensorPreferenceDialogVisibility} style={{ width: '1000px' }} header={'Adjust Sensor Preference'} modal className='p-fluid' footer={editSensorPreferenceDialogFooter} onHide={hideEditSensorPreferenceDialog}>
            <div className='field'>
              <label htmlFor='serialNumber' style={{ color: 'gray', fontSize: '18px', marginRight: '10px' }} >Serial Number: </label><label>{focusedDevice.serialNumber}</label>
            </div>

            {focusedDevice?.service === 'calyxsensingdatas' ?
              <div className='field'>
                <label htmlFor='remark' style={{ color: 'gray', fontSize: '18px', marginRight: '10px' }}>Probe Type: </label>
                <ul>
                  {deviceProbeTypes?.map(probeType => <li key={probeType} >{probeType}</li>) ||
                    <code>empty</code>
                  }
                </ul>
              </div> : null
            }

            {focusedDevice?.service === 'calyxsensingdatas' ?
              <div className='field'>
                <label htmlFor='remark' style={{ color: 'gray', fontSize: '18px', marginRight: '10px' }} >Sensor Preference: </label>
                <ShowSensorPreference />
              </div> : null
            }
          </Dialog>

          <Dialog blockScroll={true} visible={resetToDeafultSensorPreferenceDialogVisibility} style={{ width: '450px' }} header={'Reset to default sensor preference?'} modal className='p-fluid' footer={resetToDefaultSensorPreferenceDialogFooter} onHide={hideResetToDefaultSensorPreferenceDialog}>
          </Dialog>

          <Dialog blockScroll={true} visible={editProbeTypeDialogVisibility} style={{ width: '450px' }} header={'Adjust Probe Type'} modal className='p-fluid' footer={editProbeTypeDialogFooter} onHide={hideEditProbeTypeDialog}>
            {focusedDevice?.service === 'calyxsensingdatas' ?
              <ShowProbeType /> : null
            }
          </Dialog>

          {/* <Dialog blockScroll={true} visible={deviceDownlinkDialog} style={{ width: '800px' }} className='p-fluid' header={`Device downlink ${deviceDownlink.noPackages ? '(No packages)' : ''}`} modal onHide={hideDeviceDownlinkDialog}>
            <TabView>
              <TabPanel header='Request'>
                <h1>Request Pagkages</h1>
                <div className='field'>
                  <label>
                    <h3 htmlFor='reqSysInfoPkg'>Request System Information Package</h3>
                  </label>
                  <div>
                    <label>
                      <h4>last uplink pkg5:</h4>
                      <h5>payload: {JSON.stringify(deviceDownlink.pkg5.payload)}</h5>
                      <h5>time: {formatUnixTime(deviceDownlink.pkg5.time)} ({deviceDownlink.pkg5.time}) </h5>
                    </label>
                    <Button label='Send Package' icon='pi' className='p-button-text' onClick={() => doDownlink('pkg5')} />
                  </div>
                </div>
                <div className='field'>
                  <label>
                    <h3 htmlFor='reqSysInfoPkg'>Request System Parameter Package</h3>
                  </label>
                  <div>
                    <label>
                      <h4>last uplink pkg6:</h4>
                      <h5>payload: {JSON.stringify(deviceDownlink.pkg6.payload)}</h5>
                      <h5>time: {formatUnixTime(deviceDownlink.pkg6.time)} ({deviceDownlink.pkg6.time})</h5>
                    </label>
                    <label>
                      <h4>last uplink pkg7:</h4>
                      <h5>payload: {JSON.stringify(deviceDownlink.pkg7.payload)}</h5>
                      <h5>time: {formatUnixTime(deviceDownlink.pkg7.time)} ({deviceDownlink.pkg7.time})</h5>
                    </label>
                    <Button label='Send Package' icon='pi' className='p-button-text' onClick={() => doDownlink('pkg6')} />
                  </div>
                </div>
              </TabPanel>
              <TabPanel header='Pkg 11'>
                <h1>Adjust Interval</h1>
                <div className='field'>
                  <label>
                    <h3 htmlFor='regularMeasurementInterval'>Regular Measurement Interval {`= ${deviceDownlink.pkg11.regularMeasurementInterval} min`}</h3>
                  </label>
                  <div>
                    <InputText value={deviceDownlink.pkg11.regularMeasurementInterval} onChange={(e) => onDownlinkInputChange(e, 'pkg11', 'regularMeasurementInterval', 1, 255)} />
                    <Slider min={1} max={255} value={deviceDownlink.pkg11.regularMeasurementInterval} onChange={(e) => onSliderValue(e, 'pkg11', 'regularMeasurementInterval')} />
                  </div>
                </div>
                <div className='field'>
                  <label>
                    <h3 htmlFor='statusReportInterval'>Status Report Interval {`= ${deviceDownlink.pkg11.statusReportInterval} min`}</h3>
                  </label>
                  <div>
                    <InputText value={deviceDownlink.pkg11.statusReportInterval} onChange={(e) => onDownlinkInputChange(e, 'pkg11', 'statusReportInterval', 0, 65535)} />
                    <Slider min={0} max={65536} value={deviceDownlink.pkg11.statusReportInterval} onChange={(e) => onSliderValue(e, 'pkg11', 'statusReportInterval')} />
                  </div>
                </div>
                <div className='field'>
                  <label>
                    <h3 htmlFor='sleepN1'>Sleep N1 {`= ${deviceDownlink.pkg11.sleepN1 * deviceDownlink.pkg11.regularMeasurementInterval} min`}</h3>
                  </label>
                  <div>
                    <InputText value={deviceDownlink.pkg11.sleepN1} onChange={(e) => onDownlinkInputChange(e, 'pkg11', 'sleepN1', 0, 255)} />
                    <Slider min={0} max={255} value={deviceDownlink.pkg11.sleepN1} onChange={(e) => onSliderValue(e, 'pkg11', 'sleepN1')} />
                  </div>
                </div>
                <div className='field'>
                  <label>
                    <h3 htmlFor='sleepN2'>Sleep N2 {`= ${deviceDownlink.pkg11.sleepN2 * deviceDownlink.pkg11.regularMeasurementInterval} min`}</h3>
                  </label>
                  <div>
                    <InputText value={deviceDownlink.pkg11.sleepN2} onChange={(e) => onDownlinkInputChange(e, 'pkg11', 'sleepN2', 0, 255)} />
                    <Slider min={0} max={255} value={deviceDownlink.pkg11.sleepN2} onChange={(e) => onSliderValue(e, 'pkg11', 'sleepN2')} />
                  </div>
                </div>
                <div className='field'>
                  <Button label='Send Package' icon='pi' className='p-button-text' onClick={() => doDownlink('pkg11')} />
                </div>
              </TabPanel>
              <TabPanel header='Pkg 12'>
                <h1>Adjust NH3 Threshold</h1>
                <div className='field'>
                  <label>
                    <h3 htmlFor='nh3Threshold'>NH3 Threshold {`= ${deviceDownlink.pkg12.nh3Threshold} ppm`}</h3>
                  </label>
                  <div>
                    <InputText value={deviceDownlink.pkg12.nh3Threshold} onChange={(e) => onDownlinkInputChange(e, 'pkg12', 'nh3Threshold', 0, 255)} />
                    <Slider min={0} max={255} value={deviceDownlink.pkg12.nh3Threshold} onChange={(e) => onSliderValue(e, 'pkg12', 'nh3Threshold')} />
                  </div>
                </div>
                <div className='field'>
                  <Button label='Send Package' icon='pi' className='p-button-text' onClick={() => doDownlink('pkg12')} />
                </div>
              </TabPanel>
              <TabPanel header='Pkg 13'>
                <h1>Adjust Temperature Threshold</h1>
                <div className='field'>
                  <label>
                    <h3 htmlFor='tempThreshold'>Temperature Threshold  {`= ${deviceDownlink.pkg13.temperatureThreshold / 10} °C`}</h3>
                  </label>
                  <div>
                    <InputText value={deviceDownlink.pkg13.temperatureThreshold} onChange={(e) => onDownlinkInputChange(e, 'pkg13', 'temperatureThreshold', 0, 255)} />
                    <Slider min={0} max={255} value={deviceDownlink.pkg13.temperatureThreshold} onChange={(e) => onSliderValue(e, 'pkg13', 'temperatureThreshold')} />
                  </div>
                </div>
                <div className='field'>
                  <Button label='Send Package' icon='pi' className='p-button-text' onClick={() => doDownlink('pkg13')} />
                </div>
              </TabPanel>
              <TabPanel header='Pkg 14'>
                <h1>Adjust CO2 Threshold</h1>
                <div className='field'>
                  <label>
                    <h3 htmlFor='co2Threshold'>CO2 Threshold  {`= ${deviceDownlink.pkg14.co2Threshold * 10} ppm`}</h3>
                  </label>
                  <div>
                    <InputText value={deviceDownlink.pkg14.co2Threshold} onChange={(e) => onDownlinkInputChange(e, 'pkg14', 'co2Threshold', 0, 255)} />
                    <Slider min={0} max={255} value={deviceDownlink.pkg14.co2Threshold} onChange={(e) => onSliderValue(e, 'pkg14', 'co2Threshold')} />
                  </div>
                </div>
                <div className='field'>
                  <Button label='Send Package' icon='pi' className='p-button-text' onClick={() => doDownlink('pkg14')} />
                </div>
              </TabPanel>
              <TabPanel header='Pkg 15'>
                <h1>Adjust Anemoter Threshold</h1>
                <div className='field'>
                  <label>
                    <h3 htmlFor='anemoterThreshold'>Anemoter Threshold  {`= ${deviceDownlink.pkg15.anemometerThreshold / 10} m/s`}</h3>
                  </label>
                  <div>
                    <InputText value={deviceDownlink.pkg15.anemometerThreshold} onChange={(e) => onDownlinkInputChange(e, 'pkg15', 'anemometerThreshold', 0, 255)} />
                    <Slider min={0} max={255} value={deviceDownlink.pkg15.anemometerThreshold} onChange={(e) => onSliderValue(e, 'pkg15', 'anemometerThreshold')} />
                  </div>
                </div>
                <div className='field'>
                  <Button label='Send Package' icon='pi' className='p-button-text' onClick={() => doDownlink('pkg15')} />
                </div>
              </TabPanel>
              <TabPanel header='Pkg 16'>
                <h1>Adjust Sensor Type Parameter</h1>
                <div className='field'>
                  <label>
                    <h3 htmlFor='nh3Threshold'>Sensor A Type = {parseProbType(deviceDownlink.pkg16.sensorAType)}</h3>
                  </label>
                  <div>
                    <InputText value={deviceDownlink.pkg16.sensorAType} onChange={(e) => onDownlinkInputChange(e, 'pkg16', 'sensorAType', 1, 6)} />
                    <Slider min={1} max={6} value={deviceDownlink.pkg16.sensorAType} onChange={(e) => onSliderValue(e, 'pkg16', 'sensorAType')} />
                  </div>
                </div>
                <div className='field'>
                  <label>
                    <h3 htmlFor='nh3Threshold'>Sensor B Type = {parseProbType(deviceDownlink.pkg16.sensorBType)}</h3>
                  </label>
                  <div>
                    <InputText value={deviceDownlink.pkg16.sensorBType} onChange={(e) => onDownlinkInputChange(e, 'pkg16', 'sensorBType', 1, 6)} />
                    <Slider min={1} max={6} value={deviceDownlink.pkg16.sensorBType} onChange={(e) => onSliderValue(e, 'pkg16', 'sensorBType')} />
                  </div>
                </div>
                <div className='field'>
                  <label>
                    <h3 htmlFor='nh3Threshold'>Sensor C Type = {parseProbType(deviceDownlink.pkg16.sensorCType)}</h3>
                  </label>
                  <div>
                    <InputText value={deviceDownlink.pkg16.sensorCType} onChange={(e) => onDownlinkInputChange(e, 'pkg16', 'sensorCType', 1, 6)} />
                    <Slider min={1} max={6} value={deviceDownlink.pkg16.sensorCType} onChange={(e) => onSliderValue(e, 'pkg16', 'sensorCType')} />
                  </div>
                </div>
                <div className='field'>
                  <Button label='Send Package' icon='pi' className='p-button-text' onClick={() => doDownlink('pkg16')} />
                </div>
              </TabPanel>
            </TabView>
          </Dialog> */}
        </div>
      </div>
    </div>
  )
}
